import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useLocation, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
// ✅ Import shared auth utilities - SINGLE SOURCE OF TRUTH
import {
  authenticatedFetch,
  getAuthHeaders,
  isAuthenticated,
  clearStoredCredentials
} from '../../utils/api';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Accordion, AccordionTab } from 'primereact/accordion';

// 🎨 Orange Theme Colors
const BRAND_ORANGE = '#F89423';
const BRAND_ORANGE_DARK = '#ea7c1f';
const BRAND_ORANGE_LIGHT = '#fef3e2';

// ✅ API_BASE Configuration
const API_BASE = (process.env.REACT_APP_API_URL || 'https://localhost:7018').trim().replace(/\/$/, '');

// ✅ Hospital Category Options
const HOSPITAL_CATEGORY_OPTIONS = [
  { label: 'Medical', value: 'Medical' },
  { label: 'Optical', value: 'Optical' },
  { label: 'Dental', value: 'Dental' }
];

// ✅ Product Status Options
const PRODUCT_STATUS_OPTIONS = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' },
  { label: 'Draft', value: 'Draft' },
  { label: 'Archived', value: 'Archived' }
];

// ✅ Generic Category Options
const CATEGORY_OPTIONS = [
  { label: 'Insurance', value: 'insurance' },
  { label: 'Investment', value: 'investment' },
  { label: 'Savings', value: 'savings' },
  { label: 'Pension', value: 'pension' },
  { label: 'Other', value: 'other' }
];

// ✅ Entity Configuration
const ENTITY_CONFIG = {
  states: {
    title: 'States',
    apiEndpoint: `${API_BASE}/Settings/get-all-states`,
    createEndpoint: `${API_BASE}/Settings/create-State`,
    updateEndpoint: `${API_BASE}/Settings/update-State`,
    deleteEndpoint: `${API_BASE}/Settings/Delete-State`,
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
      { field: 'name', header: 'State Name', sortable: true }
    ],
    formFields: [
      { name: 'name', label: 'State Name', type: 'text', required: true, placeholder: 'e.g., Lagos, Abuja', maxLength: 100 }
    ],
    icon: 'fa-map-marker-alt',
    hasDescription: false,
    hasCategory: false,
    hasStatus: true,
    useSourceField: false
  },
  titles: {
    title: 'Titles',
    apiEndpoint: `${API_BASE}/Settings/titles`,
    createEndpoint: `${API_BASE}/Settings/create-Title`,
    updateEndpoint: `${API_BASE}/Settings/update-Title`,
    deleteEndpoint: `${API_BASE}/Settings/delete-Title`,
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
      { field: 'name', header: 'Title', sortable: true }
    ],
    formFields: [
      { name: 'name', label: 'Title', type: 'text', required: true, placeholder: 'e.g., Mr., Mrs., Dr.', maxLength: 50 }
    ],
    icon: 'fa-certificate',
    hasDescription: false,
    hasCategory: false,
    hasStatus: true,
    useSourceField: false
  },
  'agent-types': {
    title: 'Agent Types',
    apiEndpoint: `${API_BASE}/Settings/agent-types`,
    createEndpoint: `${API_BASE}/Settings/create-agent-type`,
    updateEndpoint: `${API_BASE}/Settings/update-agent-type`,
    deleteEndpoint: `${API_BASE}/Settings/delete-agent-type`,
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
      { field: 'name', header: 'Agent Type', sortable: true }
    ],
    formFields: [
      { name: 'name', label: 'Agent Type Name', type: 'text', required: true, placeholder: 'e.g., Direct Agent, Broker', maxLength: 100 }
    ],
    icon: 'fa-user-tie',
    hasDescription: true,
    hasCategory: false,
    hasStatus: true,
    useSourceField: false
  },
  'hear-about': {
    title: 'How They Heard About Us',
    apiEndpoint: `${API_BASE}/Settings/hear-about`,
    createEndpoint: `${API_BASE}/Settings/create-hear-about`,
    updateEndpoint: `${API_BASE}/Settings/update-hear-about`,
    deleteEndpoint: `${API_BASE}/Settings/delete-hear-about`,
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
      { field: 'source', header: 'Source', sortable: true }
    ],
    formFields: [
      { name: 'source', label: 'Source', type: 'text', required: true, placeholder: 'e.g., Social Media, Referral', maxLength: 100 }
    ],
    icon: 'fa-bullhorn',
    hasDescription: false,
    hasCategory: false,
    hasStatus: true,
    useSourceField: true
  },
  'business-categories': {
    title: 'Business Categories',
    apiEndpoint: `${API_BASE}/Settings/business-categories`,
    createEndpoint: `${API_BASE}/Settings/create-business-category`,
    updateEndpoint: `${API_BASE}/Settings/update-business-category`,
    deleteEndpoint: `${API_BASE}/Settings/delete-business-category`,
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
      { field: 'name', header: 'Category', sortable: true }
    ],
    formFields: [
      { name: 'name', label: 'Category Name', type: 'text', required: true, placeholder: 'e.g., Healthcare, Finance', maxLength: 100 }
    ],
    icon: 'fa-briefcase',
    hasDescription: true,
    hasCategory: false,
    hasStatus: true,
    useSourceField: false
  },
  'business-sectors': {
    title: 'Business Sectors',
    apiEndpoint: `${API_BASE}/Settings/business-sectors`,
    createEndpoint: `${API_BASE}/Settings/create-business-sector`,
    updateEndpoint: `${API_BASE}/Settings/update-business-sector`,
    deleteEndpoint: `${API_BASE}/Settings/delete-business-sector`,
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
      { field: 'name', header: 'Sector', sortable: true }
    ],
    formFields: [
      { name: 'name', label: 'Sector Name', type: 'text', required: true, placeholder: 'e.g., Public Sector, Private', maxLength: 100 }
    ],
    icon: 'fa-industry',
    hasDescription: true,
    hasCategory: false,
    hasStatus: true,
    useSourceField: false
  },
  'products-of-interest': {
    title: 'Products of Interest',
    apiEndpoint: `${API_BASE}/Settings/products-of-interest`,
    createEndpoint: `${API_BASE}/Settings/create-product`,
    updateEndpoint: `${API_BASE}/Settings/update-product`,
    deleteEndpoint: `${API_BASE}/Settings/delete-product`,
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
      { field: 'name', header: 'Product', sortable: true }
    ],
    formFields: [
      { name: 'name', label: 'Product Name', type: 'text', required: true, placeholder: 'e.g., Health Insurance, Life Insurance', maxLength: 100 }
    ],
    icon: 'fa-box',
    hasDescription: true,
    hasCategory: true,
    hasStatus: true,
    useSourceField: false
  },
  hospitals: {
    title: 'Hospitals',
    apiEndpoint: `${API_BASE}/Settings/hospitals`,
    createEndpoint: `${API_BASE}/Settings/create-hospital`,
    updateEndpoint: `${API_BASE}/Settings/update-hospital`,
    deleteEndpoint: `${API_BASE}/Settings/delete-hospital`,
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
      { field: 'name', header: 'Hospital Name', sortable: true },
      { field: 'stateName', header: 'State', sortable: true, style: { width: '120px' } },
      {
        field: 'category',
        header: 'Category',
        sortable: true,
        style: { width: '110px' },
        body: (rowData) => {
          const getCategorySeverity = (cat) => {
            switch (cat) {
              case 'Medical': return 'info';
              case 'Optical': return 'success';
              case 'Dental': return 'warning';
              default: return 'secondary';
            }
          };
          return (
            <Tag
              value={rowData.category || 'N/A'}
              severity={getCategorySeverity(rowData.category)}
              className="fw-semibold"
            />
          );
        }
      },
      { field: 'address', header: 'Address', sortable: true, style: { width: '200px' } },
    ],
    formFields: [
      { name: 'name', label: 'Hospital Name', type: 'text', required: true, placeholder: 'e.g., Lagos University Teaching Hospital', maxLength: 150 },
      { name: 'stateName', label: 'State', type: 'dropdown', required: true },
      { name: 'category', label: 'Category', type: 'dropdown', required: false, options: HOSPITAL_CATEGORY_OPTIONS },
      { name: 'address', label: 'Address', type: 'text', required: true, placeholder: 'e.g., Ikeja, Lagos', maxLength: 200 },
      { name: 'contactEmail', label: 'Contact Email', type: 'email', required: false, placeholder: 'e.g., info@hospital.com' },
      { name: 'contactPhone', label: 'Contact Phone', type: 'text', required: false, placeholder: 'e.g., +234 801 234 5678' }
    ],
    icon: 'fa-hospital',
    hasDescription: false,
    hasCategory: true,
    hasStatus: false,
    useSourceField: false
  },
  'product-categories': {
    title: 'Product Categories',
    apiEndpoint: `${API_BASE}/Settings/product-categories`,
    createEndpoint: `${API_BASE}/Settings/create-product-category`,
    updateEndpoint: `${API_BASE}/Settings/update-product-category`,
    deleteEndpoint: `${API_BASE}/Settings/delete-product-category`,
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
      { field: 'name', header: 'Category Name', sortable: true }
    ],
    formFields: [
      { name: 'name', label: 'Category Name', type: 'text', required: true, placeholder: 'e.g., Health Insurance, Life Insurance', maxLength: 100 }
    ],
    icon: 'fa-folder-open',
    hasDescription: false,
    hasCategory: false,
    hasStatus: false,
    useSourceField: false
  },
  'plan-categories': {
    title: 'Plan Categories',
    apiEndpoint: `${API_BASE}/PlanCategories/plan-categories`,
    createEndpoint: `${API_BASE}/PlanCategories/create-plan-category`,
    updateEndpoint: `${API_BASE}/PlanCategories/update-plan-category`,
    deleteEndpoint: `${API_BASE}/PlanCategories/delete-plan-category`,
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
      { field: 'categoryName', header: 'Category Name', sortable: true },
      { field: 'description', header: 'Description', sortable: true, style: { width: '200px' } },
      {
        field: 'isRequireRegister',
        header: 'Require Register',
        sortable: true,
        style: { width: '150px' },
        body: (rowData) => (
          <Tag
            value={rowData.isRequireRegister ? 'Yes' : 'No'}
            severity={rowData.isRequireRegister ? 'success' : 'secondary'}
            className="fw-semibold"
          />
        )
      },
      {
        field: 'isActive',
        header: 'Status',
        sortable: true,
        style: { width: '100px' },
        body: (rowData) => (
          <Tag
            value={rowData.isActive ? 'Active' : 'Inactive'}
            severity={rowData.isActive ? 'success' : 'danger'}
            className="fw-semibold"
          />
        )
      }
    ],
    formFields: [
      { name: 'categoryName', label: 'Category Name', type: 'text', required: true, placeholder: 'e.g., Health, Auto, Life', maxLength: 100 }
    ],
    icon: 'fa-tags',
    hasDescription: true,
    hasCategory: false,
    hasStatus: true,
    useSourceField: false,
    usesFormSubmit: true
  },
  planservices: {
    title: 'Plan Services',
    apiEndpoint: `${API_BASE}/planservice/listings`,
    createEndpoint: `${API_BASE}/planservice/create-newservice`,
    updateEndpoint: `${API_BASE}/planservice/update-services`,
    deleteEndpoint: `${API_BASE}/planservice/delete-services`,
    columns: [
      { field: 'id', header: 'S/N', sortable: false, style: { width: '80px' }, body: (rowData, { rowIndex }) => rowIndex + 1 },
      { field: 'servicename', header: 'Service Name', sortable: true }
    ],
    formFields: [
      { name: 'servicename', label: 'Service Name', type: 'text', required: true, placeholder: 'e.g., Comprehensive Health Plan', maxLength: 150 }
    ],
    icon: 'fa-concierge-bell',
    hasDescription: false,
    hasCategory: false,
    hasStatus: false,
    useSourceField: false
  },
  plandescriptionservices: {
    title: 'Description Services',
    apiEndpoint: `${API_BASE}/Plandescriptionservice/listings`,
    createEndpoint: `${API_BASE}/Plandescriptionservice/create-newservices`,
    updateEndpoint: `${API_BASE}/Plandescriptionservice/update-services`,
    deleteEndpoint: `${API_BASE}/Plandescriptionservice/delete-services`,
    columns: [
      { field: 'id', header: 'S/N', sortable: false, style: { width: '80px' }, body: (rowData, { rowIndex }) => rowIndex + 1 },
      { field: 'descriptionName', header: 'Description Name', sortable: true }
    ],
    formFields: [
      { name: 'descriptionName', label: 'Description Name', type: 'text', required: true, placeholder: 'e.g., Comprehensive Health Plan', maxLength: 150 }
    ],
    icon: 'fa-concierge-bell',
    hasDescription: false,
    hasCategory: false,
    hasStatus: false,
    useSourceField: false
  },
  providers: {
    title: 'Providers',
    apiEndpoint: `${API_BASE}/Settings/providers`,
    createEndpoint: `${API_BASE}/Settings/create-provider`,
    updateEndpoint: `${API_BASE}/Settings/update-provider`,
    deleteEndpoint: `${API_BASE}/Settings/delete-provider`,
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
      { field: 'providerName', header: 'Provider Name', sortable: true },
      { field: 'contactPerson', header: 'Contact Person', sortable: true, style: { width: '150px' } },
      { field: 'contactNumber', header: 'Phone', sortable: true, style: { width: '140px' } },
      { field: 'email', header: 'Email', sortable: true, style: { width: '180px' } },
      { field: 'isActive', header: 'Status', sortable: true, style: { width: '100px' } }
    ],
    formFields: [
      { name: 'providerName', label: 'Provider Name', type: 'text', required: true, placeholder: 'e.g., Goxi MicroInsurance', maxLength: 150 },
      { name: 'contactPerson', label: 'Contact Person', type: 'text', required: true, placeholder: 'e.g., John Doe', maxLength: 100 },
      { name: 'contactNumber', label: 'Contact Number', type: 'text', required: true, placeholder: 'e.g., +234 801 234 5678', maxLength: 20 },
      { name: 'email', label: 'Email', type: 'email', required: true, placeholder: 'e.g., info@provider.com' },
      { name: 'address', label: 'Address', type: 'text', required: false, placeholder: 'e.g., 123 Business Street, Lagos', maxLength: 200 },
      { name: 'password', label: 'Password', type: 'password', required: false, placeholder: 'Leave blank to keep current', maxLength: 100 }
    ],
    icon: 'fa-building',
    hasDescription: false,
    hasCategory: false,
    hasStatus: true,
    useSourceField: false
  },
  products: {
    title: 'Plans',
    apiEndpoint: `${API_BASE}/products/products`,
    createEndpoint: `${API_BASE}/products/create-new-product`,
    updateEndpoint: `${API_BASE}/products/update-product-plan`,
    deleteEndpoint: `${API_BASE}/products/delete-product`,
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
      { field: 'name', header: 'Plan Name', sortable: true },
      { field: 'categoryName', header: 'Category', sortable: true, style: { width: '150px' } },
      { field: 'planCategoryName', header: 'Plan Category', sortable: true, style: { width: '150px' } },
      { field: 'providerName', header: 'Provider', sortable: true, style: { width: '150px' } },
      { field: 'minimumPremium', header: 'Min Premium', sortable: true, style: { width: '130px' } },
      { field: 'familyPremium', header: 'Family Premium', sortable: true, style: { width: '140px' } },
      { field: 'status', header: 'Status', sortable: true, style: { width: '110px' } }
    ],
    formFields: [
      { name: 'name', label: 'Plan Name', type: 'text', required: true, placeholder: 'e.g., Comprehensive Health Plan', maxLength: 150 },
      { name: 'categoryId', label: 'Category', type: 'dropdown', required: true },
      { name: 'planCategoryId', label: 'Plan Category', type: 'dropdown', required: true },
      { name: 'providerId', label: 'Provider', type: 'dropdown', required: true },
      { name: 'minimumPremium', label: 'Minimum Premium', type: 'number', required: true },
      { name: 'familyPremium', label: 'Family Premium', type: 'number', required: false },
      { name: 'status', label: 'Status', type: 'dropdown', required: true },
      { name: 'Description', label: 'Description', type: 'text', required: false },
      { name: 'benefits', label: 'Benefits', type: 'textarea', required: false },
      { name: 'howItWorks', label: 'How It Works', type: 'textarea', required: false },
      { name: 'howToClaim', label: 'How To Claim', type: 'textarea', required: false }
    ],
    icon: 'fa-box-open',
    hasDescription: false,
    hasCategory: false,
    hasStatus: true,
    useSourceField: false
  },
  plansandfeatures: {
    title: 'Hmo Plans & Features',
    apiEndpoint: `${API_BASE}/products/products`,
    createEndpoint: `${API_BASE}/products/create-new-product`,
    updateEndpoint: `${API_BASE}/products/update-product-plan`,
    deleteEndpoint: `${API_BASE}/products/delete-product`,
    featuresEndpoint: `${API_BASE}/Hmoplandescriptionservice/upsert-hmo-plan-service-features`, // ✅ UPDATED ENDPOINT
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
      { field: 'name', header: 'Plan Name', sortable: true },
      { field: 'categoryName', header: 'Category', sortable: true, style: { width: '150px' } },
      { field: 'planCategoryName', header: 'Plan Category', sortable: true, style: { width: '150px' } },
      { field: 'providerName', header: 'Provider', sortable: true, style: { width: '150px' } },
      { field: 'minimumPremium', header: 'Min Premium', sortable: true, style: { width: '130px' } },
      { field: 'familyPremium', header: 'Family Premium', sortable: true, style: { width: '140px' } },
      { field: 'status', header: 'Status', sortable: true, style: { width: '110px' } }
    ],
    formFields: [
      { name: 'name', label: 'Plan Name', type: 'text', required: true, placeholder: 'e.g., Comprehensive Health Plan', maxLength: 150 },
      { name: 'categoryId', label: 'Category', type: 'dropdown', required: true },
      { name: 'planCategoryId', label: 'Plan Category', type: 'dropdown', required: true },
      { name: 'providerId', label: 'Provider', type: 'dropdown', required: true },
      { name: 'minimumPremium', label: 'Minimum Premium', type: 'number', required: true },
      { name: 'familyPremium', label: 'Family Premium', type: 'number', required: false },
      { name: 'status', label: 'Status', type: 'dropdown', required: true },
      { name: 'Description', label: 'Description', type: 'text', required: false },
      { name: 'benefits', label: 'Benefits', type: 'textarea', required: false },
      { name: 'howItWorks', label: 'How It Works', type: 'textarea', required: false },
      { name: 'howToClaim', label: 'How To Claim', type: 'textarea', required: false }
    ],
    icon: 'fa-box-open',
    hasDescription: false,
    hasCategory: false,
    hasStatus: true,
    useSourceField: false
  },
  permissions: {
    title: 'Permissions',
    apiEndpoint: `${API_BASE}/Permission/get-all-permissions`,
    createEndpoint: `${API_BASE}/Permission/create-new-permission`,
    updateEndpoint: `${API_BASE}/Permission/update-permission`,
    deleteEndpoint: `${API_BASE}/Permission/delete-permission`,
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
      { field: 'name', header: 'Permission Name', sortable: true },
      { field: 'description', header: 'Description', sortable: true, style: { width: '250px' } }
    ],
    formFields: [
      { name: 'name', label: 'Permission Name', type: 'text', required: true, placeholder: 'e.g., CanCreateUser, CanDeletePost', maxLength: 100 },
      { name: 'description', label: 'Description', type: 'textarea', required: false, placeholder: 'Brief description of this permission', maxLength: 500 }
    ],
    icon: 'fa-shield-alt',
    hasDescription: true,
    hasCategory: false,
    hasStatus: false,
    useSourceField: false
  },
  roles: {
    title: 'Roles',
    apiEndpoint: `${API_BASE}/Role/get-all-roles`,
    createEndpoint: `${API_BASE}/Role/create-new-role`,
    updateEndpoint: `${API_BASE}/Role/update-role`,
    deleteEndpoint: `${API_BASE}/Role/delete-role`,
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
      { field: 'name', header: 'Role Name', sortable: true },
      { field: 'description', header: 'Description', sortable: true, style: { width: '250px' } }
    ],
    formFields: [
      { name: 'name', label: 'Role Name', type: 'text', required: true, placeholder: 'e.g., Admin, Manager, Agent', maxLength: 100 },
      { name: 'description', label: 'Description', type: 'textarea', required: false, placeholder: 'Brief description of this role', maxLength: 500 }
    ],
    icon: 'fa-user-tag',
    hasDescription: true,
    hasCategory: false,
    hasStatus: false,
    useSourceField: false
  },
  admins: {
    title: 'Admin Users',
    apiEndpoint: `${API_BASE}/Admin/get-all-admins`,
    createEndpoint: `${API_BASE}/Admin/create-admin`,
    updateEndpoint: `${API_BASE}/Admin/update-admin`,
    deleteEndpoint: `${API_BASE}/Admin/delete-admin`,
    columns: [
      { field: 'id', header: 'ID', sortable: true, style: { width: '70px' } },
      { field: 'name', header: 'Name', sortable: true, style: { width: '100px' } },
      { field: 'email', header: 'Email', sortable: true, style: { width: '100px' } },
      { field: 'roleName', header: 'Role', sortable: true, style: { width: '120px' } },
    ],
    formFields: [
      { name: 'name', label: 'Full Name', type: 'text', required: true, placeholder: 'e.g., John Doe', maxLength: 100 },
      { name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: 'e.g., john@company.com', maxLength: 150 },
      { name: 'roleId', label: 'Role', type: 'dropdown', required: true },
      { name: 'password', label: 'Password', type: 'password', required: false, placeholder: 'Leave blank to auto-generate', maxLength: 100 },
      { name: 'isActive', label: 'Active', type: 'boolean', required: false }
    ],
    icon: 'fa-user-shield',
    hasDescription: false,
    hasCategory: false,
    hasStatus: true,
    useSourceField: false,
    usesFormSubmit: true
  }
};

// ✅ Bootstrap Select Component
const BootstrapSelect = ({ options, value, onChange, placeholder, disabled, className, name, ...props }) => {
  const handleChange = (e) => {
    const val = e.target.value === '' ? null : e.target.value;
    onChange({ target: { name, value: val } });
  };
  return (
    <select
      name={name}
      className={`form-select ${className || ''}`}
      value={value ?? ''}
      onChange={handleChange}
      disabled={disabled}
      {...props}
      style={{ width: '100%', ...props.style }}
    >
      <option value="">{placeholder || 'Select...'}</option>
      {options?.map(opt => (
        <option key={String(opt.value)} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
};

// ✅ Bootstrap Multi-Select Component
const BootstrapMultiSelect = ({ options, value, onChange, placeholder, disabled, ...props }) => {
  const handleChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, option => option.value);
    onChange({ target: { value: selected } });
  };
  return (
    <select
      multiple
      className="form-select"
      value={value || []}
      onChange={handleChange}
      disabled={disabled}
      {...props}
      style={{ width: '100%', minHeight: '120px', ...props.style }}
    >
      {options?.map(opt => (
        <option key={String(opt.value)} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
};

// ✅ Helper: Get category icon emoji
const getCategoryIcon = (category) => {
  switch (category) {
    case 'Medical': return '🏥';
    case 'Optical': return '👁️';
    case 'Dental': return '🦷';
    default: return '';
  }
};

const getCategorySeverity = (category) => {
  switch (category) {
    case 'Medical': return 'info';
    case 'Optical': return 'success';
    case 'Dental': return 'warning';
    default: return 'secondary';
  }
};

// ✅ Helper: Format file size for display
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

function AdminCRUDPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { entity } = useParams();

  // Sidebar State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Loading & Data State
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');

  // Filter States for Hospitals
  const [stateFilter, setStateFilter] = useState(null);

  // Modal State
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const deleteRef = useRef(null);

  // Dropdown Options State
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [planCategoryOptions, setPlanCategoryOptions] = useState([]);
  const [providerOptions, setProviderOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);

  // Hospital Selection State for Products
  const [selectedStateForHospitals, setSelectedStateForHospitals] = useState('');
  const [hospitalOptions, setHospitalOptions] = useState([]);
  const [selectedHospitals, setSelectedHospitals] = useState([]);
  const [allHospitals, setAllHospitals] = useState([]);

  // PDF file state (direct File object - NO BASE64)
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const pdfInputRef = useRef(null);

  // ✅ Image file state for Plan Categories
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const imageInputRef = useRef(null);
  const abortControllerRef = useRef(null);

  // 🚀 NEW: Plan Services State for HMO Plans & Features
  const [planServicesList, setPlanServicesList] = useState([]);
  const [selectedPlanServices, setSelectedPlanServices] = useState([]);
  const [planDescriptionServicesMap, setPlanDescriptionServicesMap] = useState({});
  const [servicesLoading, setServicesLoading] = useState(false);
  const [existingPlanServices, setExistingPlanServices] = useState([]);

  const config = ENTITY_CONFIG[entity] || ENTITY_CONFIG['products-of-interest'];
  const getPrimaryFieldName = () => config.formFields[0]?.name || (config.useSourceField ? 'source' : 'name');
  const getPrimaryFieldValue = (item) => {
    const fieldName = getPrimaryFieldName();
    return item[fieldName] || item.source || item.name;
  };

  // 🚀 NEW: Fetch Plan Services List
  const fetchPlanServicesList = useCallback(async () => {
    try {
      setServicesLoading(true);
      const response = await authenticatedFetch(`${API_BASE}/planservice/listings`, 'POST', {});
      if (response?.success === true || response?.status === 'success') {
        const services = (response.data || response.result || []).map(service => ({
          id: service.Id || service.id,
          serviceName: service.ServiceName || service.servicename
        }));
        setPlanServicesList(services);
      }
    } catch (error) {
      console.error('Failed to fetch plan services:', error);
    } finally {
      setServicesLoading(false);
    }
  }, []);

  // 🚀 NEW: Fetch Plan Description Services for a specific Plan Service
  const fetchPlanDescriptionServices = useCallback(async (planServiceId) => {
    try {
      const response = await authenticatedFetch(
        `${API_BASE}/Plandescriptionservice/listings?planServiceId=${planServiceId}`,
        'POST',
        {}
      );
      if (response?.success === true || response?.status === 'success') {
        const descriptions = (response.data || response.result || []).map(desc => ({
          id: desc.Id || desc.id,
          descriptionName: desc.DescriptionName || desc.descriptionName,
          selected: false,
          narrator: '',
          amounts: 0,
          familyType: '',
          numberOfFamilyMembers: null
        }));
        setPlanDescriptionServicesMap(prev => ({
          ...prev,
          [planServiceId]: descriptions
        }));
      }
    } catch (error) {
      console.error('Failed to fetch plan description services:', error);
    }
  }, []);

  // 🚀 NEW: Fetch Existing Plan Service Descriptions from database
  const fetchExistingPlanServiceDescriptions = useCallback(async (hmoPlanId) => {
    if (!hmoPlanId) return;
    try {
      const response = await authenticatedFetch(
        `${API_BASE}/Hmoplandescriptionservice/get-by-hmo-plan`,
        'POST',
        { HmoPlanId: hmoPlanId }
      );
      if (response?.success === true || response?.status === 'success') {
        const existingServices = response.data || response.result || [];
        setExistingPlanServices(existingServices);
        
        // Pre-populate selected services from existing data
        const selectedServices = [];
        const descriptionMap = {};
        
        existingServices.forEach(item => {
          const serviceId = item.planServiceId || item.planserviceid || item.PlanServiceId;
          const descriptionId = item.planServiceDescriptionId || item.planservicedescriptionid || item.PlanServiceDescriptionId;
          
          if (serviceId && !selectedServices.includes(serviceId)) {
            selectedServices.push(serviceId);
          }
          
          if (serviceId) {
            if (!descriptionMap[serviceId]) {
              descriptionMap[serviceId] = [];
            }
            const existingDesc = descriptionMap[serviceId].find(d => String(d.id) === String(descriptionId));
            if (!existingDesc) {
              descriptionMap[serviceId].push({
                id: descriptionId,
                descriptionName: item.descriptionName || item.DescriptionName || 'Description',
                selected: true,
                narrator: item.narration || item.naration || item.Naration || '',
                amounts: item.amounts || item.Amounts || 0,
                familyType: item.familyType || item.FamilyType || '',
                numberOfFamilyMembers: item.numberOfFamilyMembers || item.NumberOfFamilyMembers || null,
                isExisting: true
              });
            }
          }
        });
        
        setSelectedPlanServices(selectedServices);
        
        // Fetch full descriptions for each selected service and merge with existing
        for (const serviceId of selectedServices) {
          await fetchPlanDescriptionServices(serviceId);
        }
        
        // Merge existing data with fetched descriptions
        setPlanDescriptionServicesMap(prev => {
          const merged = { ...prev };
          Object.keys(descriptionMap).forEach(serviceId => {
            if (merged[serviceId]) {
              merged[serviceId] = merged[serviceId].map(desc => {
                const existing = descriptionMap[serviceId].find(d => String(d.id) === String(desc.id));
                if (existing) {
                  return {
                    ...desc,
                    selected: true,
                    narrator: existing.narrator,
                    amounts: existing.amounts,
                    familyType: existing.familyType,
                    numberOfFamilyMembers: existing.numberOfFamilyMembers,
                    isExisting: true
                  };
                }
                return desc;
              });
            } else {
              merged[serviceId] = descriptionMap[serviceId];
            }
          });
          return merged;
        });
      }
    } catch (error) {
      console.error('Failed to fetch existing plan service descriptions:', error);
    }
  }, [fetchPlanDescriptionServices]);

  // 🚀 NEW: Handlers for Plan Services
  const handlePlanServiceSelect = (serviceId) => {
    if (serviceId && !selectedPlanServices.includes(serviceId)) {
      setSelectedPlanServices([...selectedPlanServices, serviceId]);
      fetchPlanDescriptionServices(serviceId);
    }
  };

  const handleRemovePlanService = (serviceId) => {
    setSelectedPlanServices(selectedPlanServices.filter(id => String(id) !== String(serviceId)));
    setPlanDescriptionServicesMap(prev => {
      const newMap = { ...prev };
      delete newMap[serviceId];
      return newMap;
    });
  };

  const handleDescriptionServiceToggle = (planServiceId, descId, checked) => {
    setPlanDescriptionServicesMap(prev => ({
      ...prev,
      [planServiceId]: prev[planServiceId].map(desc =>
        desc.id === descId ? { ...desc, selected: checked } : desc
      )
    }));
  };

  const handleDescriptionNarratorChange = (planServiceId, descId, narrator) => {
    setPlanDescriptionServicesMap(prev => ({
      ...prev,
      [planServiceId]: prev[planServiceId].map(desc =>
        desc.id === descId ? { ...desc, narrator: narrator } : desc
      )
    }));
  };

  // ✅ Load Dropdown Options
  const loadDropdownOptions = useCallback(async () => {
    try {
      const authHeaders = getAuthHeaders();
      if (!authHeaders) {
        toast.error('Authentication required. Please log in again.');
        navigate('/admin-login', { replace: true });
        return;
      }

      if (entity === 'products' || entity === 'plansandfeatures') {
        const catResponse = await authenticatedFetch(`${API_BASE}/Settings/product-categories`, 'POST', {});
        if (catResponse?.success === true || catResponse?.status === 'success') {
          const categories = (catResponse.data || catResponse.result || []).map(cat => ({
            label: cat.name || cat.Name,
            value: cat.id || cat.Id
          }));
          setCategoryOptions(categories);
        }

        const planCatResponse = await authenticatedFetch(`${API_BASE}/PlanCategories/plan-categories`, 'POST', {});
        if (planCatResponse?.success === true || planCatResponse?.status === 'success') {
          const planCategories = (planCatResponse.data || planCatResponse.result || []).map(cat => ({
            label: cat.categoryName || cat.CategoryName,
            value: cat.id || cat.Id
          }));
          setPlanCategoryOptions(planCategories);
        }

        const provResponse = await authenticatedFetch(`${API_BASE}/Settings/providers`, 'POST', {});
        if (provResponse?.success === true || provResponse?.status === 'success') {
          const providers = (provResponse.data || provResponse.result || []).map(prov => ({
            label: prov.providerName || prov.ProviderName || prov.name,
            value: prov.id || prov.Id
          }));
          setProviderOptions(providers);
        }

        const stateResponse = await authenticatedFetch(`${API_BASE}/Settings/get-all-states`, 'POST', {});
        if (stateResponse?.success === true || stateResponse?.status === 'success') {
          const states = (stateResponse.data || stateResponse.result || []).map(state => ({
            label: state.name || state.Name,
            value: state.name || state.Name
          }));
          setStateOptions([{ label: 'All States', value: '' }, ...states]);
        }
      }

      if (entity === 'hospitals') {
        const stateResponse = await authenticatedFetch(`${API_BASE}/Settings/get-all-states`, 'POST', {});
        if (stateResponse?.success === true || stateResponse?.status === 'success') {
          const states = (stateResponse.data || stateResponse.result || []).map(state => ({
            label: state.name || state.Name,
            value: state.name || state.Name
          }));
          setStateOptions([{ label: 'All States', value: '' }, ...states]);
        }
      }

      if (entity === 'admins') {
        const roleResponse = await authenticatedFetch(`${API_BASE}/Role/get-all-roles`, 'POST', {});
        if (roleResponse?.success === true || roleResponse?.status === 'success') {
          const roles = (roleResponse.data || roleResponse.result || []).map(role => ({
            label: role.name || role.Name,
            value: role.id || role.Id
          }));
          setRoleOptions(roles);
        }
      }
    } catch (error) {
      console.error('Failed to load dropdown options:', error);
      toast.error('Failed to load dropdown data');
    }
  }, [entity, navigate]);

  // ✅ Load Hospitals by State
  const loadHospitalsByState = useCallback(async (stateName) => {
    try {
      const authHeaders = getAuthHeaders();
      if (!authHeaders) {
        toast.error('Authentication required. Please log in again.');
        navigate('/admin-login', { replace: true });
        return;
      }

      const payload = stateName && typeof stateName === 'string' && stateName.trim()
        ? { state: stateName.trim() }
        : {};

      const response = await authenticatedFetch(`${API_BASE}/Settings/hospitals`, 'POST', payload);
      if (response?.success === true || response?.status === 'success') {
        const hospitals = (response.data || response.result || []).map(hospital => {
          const name = hospital.name || hospital.Name || hospital.hospitalName || 'Unknown';
          const category = hospital.category || hospital.Category || 'N/A';
          const categoryIcon = getCategoryIcon(category);
          return {
            label: `${categoryIcon} ${name} - ${category}`,
            value: hospital.id || hospital.Id,
            state: hospital.stateName || hospital.StateName,
            category: category,
            rawName: name
          };
        });
        setHospitalOptions(hospitals);
        setAllHospitals(hospitals);
      } else {
        toast.error(response?.message || 'Failed to load hospitals');
      }
    } catch (error) {
      console.error('Failed to load hospitals:', error);
      toast.error('Failed to load hospitals for selected state');
    }
  }, [navigate]);

  // ✅ Load Data from API
  const loadData = useCallback(async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    const authHeaders = getAuthHeaders();
    if (!authHeaders) {
      toast.warning('Session expired. Please log in again.');
      clearStoredCredentials();
      navigate('/admin-login', { replace: true });
      return;
    }

    try {
      setLoading(true);
      const filterPayload = entity === 'hospitals' ? {
        state: stateFilter || null,
        search: globalFilter || null
      } : {
        PageNumber: 1,
        PageSize: 100 // ✅ Explicitly request 100 records to avoid default 10 limit
      };

      const response = await Promise.race([
        authenticatedFetch(config.apiEndpoint, 'POST', filterPayload, abortControllerRef.current.signal),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), 10000))
      ]);

      if (response?.success === true || response?.status === 'success') {
        const apiData = response.data || response.result || [];
        const normalizedData = apiData.map(item => {
          const normalized = {
            id: item.id || item.Id,
            name: item.name || item.Name || item.source,
            source: item.source || item.Source,
            description: item.description || item.Description,
            category: item.category || item.Category,
            isActive: item.isActive !== false && item.IsActive !== false
          };

          if (entity === 'hospitals') {
            normalized.stateName = item.stateName || item.StateName || item.state;
            normalized.stateId = item.stateId || item.StateId;
            normalized.address = item.address || item.Address;
            normalized.contactPhone = item.contactPhone || item.ContactPhone;
            normalized.contactEmail = item.contactEmail || item.ContactEmail;
            normalized.category = item.category || item.Category;
          }
          if (entity === 'providers') {
            normalized.providerName = item.providerName || item.ProviderName;
            normalized.name = normalized.providerName;
            normalized.contactPerson = item.contactPerson || item.ContactPerson;
            normalized.contactNumber = item.contactNumber || item.ContactNumber;
            normalized.email = item.email || item.Email;
            normalized.address = item.address || item.Address;
          }
          if (entity === 'product-categories') {
            normalized.code = item.code || item.Code;
          }
          if (entity === 'plan-categories') {
            normalized.categoryName = item.categoryName || item.CategoryName;
            normalized.planCategoryUid = item.planCategoryUid || item.PlanCategoryUid;
            normalized.name = normalized.categoryName;
            normalized.description = item.description || item.Description || '';
            normalized.isRequireRegister = item.isRequireRegister !== false;
            normalized.isActive = item.isActive !== false;
            normalized.image = item.image || item.Image;
          }
          if (entity === 'planservices') {
            normalized.id = item.Id || item.id;
            normalized.servicename = item.ServiceName || item.servicename;
            normalized.createddate = item.CreatedDate || item.createddate;
            normalized.isActive = item.IsActive !== false;
          }
          if (entity === 'plandescriptionservices') {
            normalized.id = item.Id || item.id;
            normalized.descriptionName = item.DescriptionName || item.descriptionName;
            normalized.createddate = item.CreatedDate || item.createddate;
          }
          if (entity === 'products' || entity === 'plansandfeatures') {
            normalized.id = item.Id || item.id;
            normalized.name = item.Name || item.name;
            normalized.categoryId = item.CategoryId || item.categoryId;
            normalized.planCategoryId = item.PlanCategoryId || item.planCategoryId;
            normalized.planCategoryName = item.PlanCategoryName || item.planCategoryName;
            normalized.providerId = item.ProviderId || item.providerId;
            normalized.minimumPremium = item.MinimumPremium || item.minimumPremium;
            normalized.familyPremium = item.FamilyPremium || item.familyPremium;
            normalized.status = item.Status || item.status;
            normalized.policyDocument = item.PolicyDocument || item.policyDocument;
            normalized.benefits = item.Benefits || item.benefits;
            normalized.howItWorks = item.HowItWorks || item.howItWorks;
            normalized.howToClaim = item.HowToClaim || item.howToClaim;
            normalized.createdAt = item.CreatedAt || item.createdAt;
            normalized.categoryName = item.CategoryName || item.categoryName;
            normalized.providerName = item.ProviderName || item.providerName;
            normalized.stateName = item.StateName || item.stateName;
            normalized.hospitals = item.Hospitals || item.hospitals || [];
          }
          if (entity === 'permissions' || entity === 'roles') {
            normalized.id = item.Id || item.id;
            normalized.name = item.Name || item.name;
            normalized.description = item.Description || item.description;
            normalized.createdAt = item.CreatedAt || item.createdAt;
          }
          if (entity === 'admins') {
            normalized.id = item.id || item.Id;
            normalized.name = item.name || item.Name;
            normalized.email = item.email || item.Email;
            normalized.roleId = item.roleId || item.RoleId;
            normalized.roleName = item.roleName || item.RoleName;
            normalized.isActive = item.isActive !== false;
            normalized.createdAt = item.createdAt || item.CreatedAt;
            normalized.lastLogin = item.lastLogin || item.LastLogin;
          }

          return normalized;
        });

        setData(normalizedData);
        setFilteredData(normalizedData);
      } else if (response?.success === false || response?.status === 'fail' || response?.status === 'error') {
        const msg = response?.message?.toLowerCase() || '';
        if (msg.includes('expired') || msg.includes('not found') || msg.includes('missing authorization') || msg.includes('invalid token') || msg.includes('invalid secret')) {
          toast.warning(response.message || 'Session expired');
          clearStoredCredentials();
          navigate('/admin-login', { replace: true });
          return;
        }
        throw new Error(response.message || 'API request failed');
      } else {
        throw new Error('Invalid API response');
      }
      setLoading(false);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Failed to load data', error);
      const errMsg = error.message?.toLowerCase() || '';
      if (errMsg.includes('invalid token') || errMsg.includes('invalid secret') || errMsg.includes('authorization')) {
        toast.error('Authentication failed. Please log in again.');
        clearStoredCredentials();
        navigate('/admin-login', { replace: true });
        return;
      }
      toast.error(error.message || 'Failed to load data');
      setData([]);
      setFilteredData([]);
      setLoading(false);
    }
  }, [entity, config, navigate, stateFilter, globalFilter]);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    loadData();
    loadDropdownOptions();
    return () => abortControllerRef.current?.abort();
  }, [loadData, loadDropdownOptions]);

  useEffect(() => {
    if (entity === 'hospitals') {
      const debounce = setTimeout(() => loadData(), 150);
      return () => clearTimeout(debounce);
    } else {
      if (globalFilter.trim() === '') {
        setFilteredData(data);
      } else {
        const filterLower = globalFilter.toLowerCase();
        const filtered = data.filter(item => {
          return Object.values(item).some(value =>
            String(value).toLowerCase().includes(filterLower)
          );
        });
        setFilteredData(filtered);
      }
    }
  }, [globalFilter, data, entity, loadData, stateFilter]);

  const toggleDropdown = (path) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) newSet.delete(path);
      else newSet.add(path);
      return newSet;
    });
  };

  const isExpanded = (path) => expandedItems.has(path);

  const handleCreate = async () => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders) {
      toast.error('Authentication required. Please log in again.');
      navigate('/admin-login', { replace: true });
      return;
    }

    setEditingItem(null);
    if (entity === 'admins' && roleOptions.length === 0) await loadDropdownOptions();

    setFormData({
      isActive: true,
      status: 'Active',
      roleId: roleOptions[0]?.value ?? null,
      category: entity === 'hospitals' ? null : null,
      isRequireRegister: true
    });
    setFormErrors({});
    setSelectedHospitals([]);
    setSelectedStateForHospitals('');
    setHospitalOptions([]);
    setSelectedPdfFile(null);
    setSelectedImageFile(null);
    setExistingPlanServices([]);

    if (entity === 'plansandfeatures') {
      setSelectedPlanServices([]);
      setPlanDescriptionServicesMap({});
      await fetchPlanServicesList();
    }

    setModalVisible(true);
  };

  const handleEdit = async (item) => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders) {
      toast.error('Authentication required. Please log in again.');
      navigate('/admin-login', { replace: true });
      return;
    }

    setEditingItem(item);
    setFormData({ ...item, isActive: item.isActive !== false, status: item.status || 'Active' });
    setFormErrors({});
    setSelectedPdfFile(null);
    setSelectedImageFile(null);

    if (entity === 'plansandfeatures') {
      setSelectedPlanServices([]);
      setPlanDescriptionServicesMap({});
      setExistingPlanServices([]);
      await fetchPlanServicesList();
      await fetchExistingPlanServiceDescriptions(item.id);
      
      if (item.planServices && Array.isArray(item.planServices)) {
        const selectedIds = item.planServices.map(ps => ps.ServiceId || ps.serviceId || ps.id);
        setSelectedPlanServices(selectedIds);
        selectedIds.forEach(serviceId => {
          fetchPlanDescriptionServices(serviceId);
        });
      }
    }

    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingItem(null);
    setFormErrors({});
    setSelectedPdfFile(null);
    setSelectedImageFile(null);
    setSelectedPlanServices([]);
    setPlanDescriptionServicesMap({});
    setExistingPlanServices([]);
  };

  const handleDelete = (event, item) => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders) {
      toast.error('Authentication required. Please log in again.');
      navigate('/admin-login', { replace: true });
      return;
    }

    confirmPopup({
      target: event.currentTarget,
      message: `Are you sure you want to delete "${getPrimaryFieldValue(item)}"?`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          let deletePayload = { id: item.id, name: config.useSourceField ? item.source : item.name };
          if (entity === 'plan-categories') {
            deletePayload = { planCategoryUid: item.planCategoryUid };
          }
          if (entity === 'planservices') {
            deletePayload = { Id: item.id, ServiceName: item.servicename };
          }
          if (entity === 'plandescriptionservices') {
            deletePayload = { Id: item.id, DescriptionName: item.descriptionName };
          }

          const response = await authenticatedFetch(config.deleteEndpoint, 'POST', deletePayload);
          if (response?.success === true || response?.status === 'success') {
            setData(prev => prev.filter(d => d.id !== item.id));
            toast.success('Deleted successfully');
          } else {
            const msg = response?.message?.toLowerCase() || '';
            if (msg.includes('invalid token') || msg.includes('invalid secret') || msg.includes('authorization')) {
              toast.error('Session expired. Please log in again.');
              clearStoredCredentials();
              navigate('/admin-login', { replace: true });
              return;
            }
            throw new Error(response.message || 'Delete failed');
          }
        } catch (error) {
          console.error('Delete error:', error);
          const errMsg = error.message?.toLowerCase() || '';
          if (errMsg.includes('invalid token') || errMsg.includes('invalid secret')) {
            toast.error('Authentication failed. Please log in again.');
            clearStoredCredentials();
            navigate('/admin-login', { replace: true });
            return;
          }
          toast.error(error.message || 'Delete failed');
        }
      },
      reject: () => { }
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  // ✅ Handle PDF file selection - DIRECT FILE, NO BASE64
  const handlePdfFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Please select a PDF file only');
        setFormErrors(prev => ({ ...prev, policyDocument: 'Only PDF files are allowed' }));
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        setFormErrors(prev => ({ ...prev, policyDocument: 'File size must be less than 10MB' }));
        return;
      }
      setSelectedPdfFile(file);
      setFormData(prev => ({ ...prev, policyDocumentFile: file }));
      setFormErrors(prev => ({ ...prev, policyDocument: '' }));
    }
  };

  const handleRemovePdf = () => {
    setSelectedPdfFile(null);
    setFormData(prev => ({ ...prev, policyDocumentFile: null }));
    if (pdfInputRef.current) pdfInputRef.current.value = '';
  };

  // ✅ Handle Image file selection for Plan Categories
  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file only');
        setFormErrors(prev => ({ ...prev, image: 'Only image files are allowed' }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        setFormErrors(prev => ({ ...prev, image: 'Image size must be less than 5MB' }));
        return;
      }
      setSelectedImageFile(file);
      setFormErrors(prev => ({ ...prev, image: '' }));
    }
  };

  const handleRemoveImage = () => {
    setSelectedImageFile(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  // ✅ FIXED VALIDATION FUNCTION
  const validateForm = () => {
    const errors = {};
    const fieldName = getPrimaryFieldName();
    const fieldValue = formData[fieldName]?.toString().trim();

    // 1. Primary Field Validation
    if (!fieldValue) {
      errors[fieldName] = `${config.formFields[0]?.label || 'Name'} is required`;
    } else if (fieldValue.length < 3) {
      errors[fieldName] = 'Must be at least 3 characters';
    }

    // 2. Duplicate Check (FIXED: Use String() to prevent false positives when ID types differ)
    const isDuplicate = data.some(item => {
      const itemValue = getPrimaryFieldValue(item);
      const isSameName = itemValue?.toString().toLowerCase() === fieldValue?.toLowerCase();
      const isDifferentId = String(item.id) !== String(editingItem?.id);
      return isSameName && isDifferentId;
    });
    if (isDuplicate) errors[fieldName] = 'This already exists';

    // 3. Entity-Specific Validation
    if (entity === 'products' || entity === 'plansandfeatures') {
      if (formData.categoryId == null || formData.categoryId === '') errors.categoryId = 'Category is required';
      if (formData.planCategoryId == null || formData.planCategoryId === '') errors.planCategoryId = 'Plan Category is required';
      if (formData.providerId == null || formData.providerId === '') errors.providerId = 'Provider is required';
      
      const minPremium = parseFloat(formData.minimumPremium);
      if (formData.minimumPremium == null || formData.minimumPremium === '' || isNaN(minPremium) || minPremium <= 0) {
        errors.minimumPremium = 'Minimum premium must be greater than 0';
      }
      
      if (!formData.status || formData.status === '') errors.status = 'Status is required';
      
      // 🚀 NEW: Validate that at least one plan service is selected for plansandfeatures
      if (entity === 'plansandfeatures' && selectedPlanServices.length === 0) {
        errors.planServices = 'At least one Plan Service must be selected.';
      }
    }

    if (entity === 'hospitals') {
      if (!formData.stateName?.toString().trim()) errors.stateName = 'State is required';
    }

    if (entity === 'providers') {
      if (!formData.contactPerson?.toString().trim()) errors.contactPerson = 'Contact Person is required';
      if (!formData.contactNumber?.toString().trim()) errors.contactNumber = 'Contact Number is required';
      if (!formData.email?.toString().trim()) errors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email format';
      if (!editingItem && !formData.password?.toString().trim()) errors.password = 'Password is required for new providers';
    }

    if (entity === 'admins') {
      if (!formData.email?.toString().trim()) errors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email format';
      if (formData.roleId == null || formData.roleId === '') errors.roleId = 'Role is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ✅ Submit Form - FormData for products/admins/plan-categories (file upload), JSON for others
  const handleSubmit = async (e) => {
    e.preventDefault();
    const authHeaders = getAuthHeaders();
    if (!authHeaders) {
      toast.error('Authentication credentials missing. Please log in again.');
      clearStoredCredentials();
      navigate('/admin-login', { replace: true });
      return;
    }

    if (!validateForm()) {
      toast.error('Please fix the form errors before submitting.');
      return;
    }

    try {
      setSubmitting(true);

      // ✅ PRODUCTS: Use FormData for file upload
      if (entity === 'products') {
        const formDataObj = new FormData();
        formDataObj.append('Name', formData.name || '');
        formDataObj.append('CategoryId', formData.categoryId?.toString() || '');
        formDataObj.append('PlanCategoryId', formData.planCategoryId?.toString() || '');
        formDataObj.append('ProviderId', formData.providerId?.toString() || '');
        formDataObj.append('MinimumPremium', formData.minimumPremium?.toString() || '0');
        formDataObj.append('FamilyPremium', formData.familyPremium?.toString() || '0');
        formDataObj.append('Status', formData.status || 'Active');
        formDataObj.append('Benefits', formData.benefits || '');
        formDataObj.append('HowItWorks', formData.howItWorks || '');
        formDataObj.append('HowToClaim', formData.howToClaim || '');
        if (selectedPdfFile) {
          formDataObj.append('policyDocumentFile', selectedPdfFile);
        }
        if (selectedHospitals?.length > 0) {
          selectedHospitals.forEach(id => {
            formDataObj.append('HospitalIds', id.toString());
          });
        }
        if (editingItem?.id) {
          formDataObj.append('Id', editingItem.id.toString());
        }

        const endpoint = editingItem ? config.updateEndpoint : config.createEndpoint;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: authHeaders,
          body: formDataObj
        });
        const result = await response.json();
        const resultMsg = result?.message?.toLowerCase() || '';

        if (resultMsg.includes('invalid token') || resultMsg.includes('invalid secret') || resultMsg.includes('authorization')) {
          toast.error('Session expired. Please log in again.');
          clearStoredCredentials();
          navigate('/admin-login', { replace: true });
          return;
        }

        if (result?.success === true || result?.status === 'success') {
          if (editingItem) {
            setData(prev => prev.map(item =>
              item.id === editingItem.id ? { ...item, ...formData } : item
            ));
            toast.success('Product updated successfully');
          } else {
            await loadData();
            toast.success('Product created successfully');
          }
          handleCloseModal();
          setSelectedHospitals([]);
          setSelectedStateForHospitals('');
          setSelectedPdfFile(null);
        } else {
          throw new Error(result.message || 'Request failed');
        }
      }
      // ✅ PLAN CATEGORIES: Use FormData for file upload
      else if (entity === 'plan-categories') {
        const formDataObj = new FormData();
        formDataObj.append('CategoryName', formData.categoryName || '');
        formDataObj.append('Description', formData.description || '');
        formDataObj.append('IsRequireRegister', formData.isRequireRegister !== false);
        if (editingItem) {
          formDataObj.append('PlanCategoryUid', editingItem.planCategoryUid);
        }
        if (selectedImageFile) {
          formDataObj.append('imageFile', selectedImageFile);
        }

        const endpoint = editingItem ? config.updateEndpoint : config.createEndpoint;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: authHeaders,
          body: formDataObj
        });
        const result = await response.json();
        const resultMsg = result?.message?.toLowerCase() || '';

        if (resultMsg.includes('invalid token') || resultMsg.includes('invalid secret') || resultMsg.includes('authorization')) {
          toast.error('Session expired. Please log in again.');
          clearStoredCredentials();
          navigate('/admin-login', { replace: true });
          return;
        }

        if (result?.success === true || result?.status === 'success') {
          if (editingItem) {
            setData(prev => prev.map(item =>
              item.planCategoryUid === editingItem.planCategoryUid ? { ...item, ...formData, image: result.data?.image } : item
            ));
            toast.success('Plan category updated successfully');
          } else {
            await loadData();
            toast.success('Plan category created successfully');
          }
          handleCloseModal();
          setSelectedImageFile(null);
        } else {
          throw new Error(result.message || 'Request failed');
        }
      }
      // 🚀 HMO PLANS & FEATURES: Two-step process
      else if (entity === 'plansandfeatures') {
        let hmoPlanId = editingItem?.id;

        // 1. If creating a new plan, save the product first
        if (!editingItem) {
          const productFormData = new FormData();
          productFormData.append('Name', formData.name || '');
          productFormData.append('CategoryId', formData.categoryId?.toString() || '');
          productFormData.append('PlanCategoryId', formData.planCategoryId?.toString() || '');
          productFormData.append('ProviderId', formData.providerId?.toString() || '');
          productFormData.append('MinimumPremium', formData.minimumPremium?.toString() || '0');
          productFormData.append('FamilyPremium', formData.familyPremium?.toString() || '0');
          productFormData.append('Status', formData.status || 'Active');
          productFormData.append('Benefits', formData.benefits || '');
          productFormData.append('HowItWorks', formData.howItWorks || '');
          productFormData.append('HowToClaim', formData.howToClaim || '');
          if (selectedPdfFile) {
            productFormData.append('policyDocumentFile', selectedPdfFile);
          }
          if (selectedHospitals?.length > 0) {
            selectedHospitals.forEach(id => {
              productFormData.append('HospitalIds', id.toString());
            });
          }

          const productResponse = await fetch(`${API_BASE}/products/create-new-product`, {
            method: 'POST',
            headers: authHeaders,
            body: productFormData
          });
          const productResult = await productResponse.json();
          
          if (productResult?.success === true || productResult?.status === 'success') {
            // ✅ ROBUST ID EXTRACTION: Check all common backend response shapes
            hmoPlanId = productResult.data?.Id || productResult.data?.id || 
                        productResult.Id || productResult.id || 
                        productResult.result?.Id || productResult.result?.id;
          } else {
            throw new Error(productResult?.message || 'Failed to create product');
          }
        } else {
          // Update existing product
          const productFormData = new FormData();
          productFormData.append('Id', editingItem.id.toString());
          productFormData.append('Name', formData.name || '');
          productFormData.append('CategoryId', formData.categoryId?.toString() || '');
          productFormData.append('PlanCategoryId', formData.planCategoryId?.toString() || '');
          productFormData.append('ProviderId', formData.providerId?.toString() || '');
          productFormData.append('MinimumPremium', formData.minimumPremium?.toString() || '0');
          productFormData.append('FamilyPremium', formData.familyPremium?.toString() || '0');
          productFormData.append('Status', formData.status || 'Active');
          productFormData.append('Benefits', formData.benefits || '');
          productFormData.append('HowItWorks', formData.howItWorks || '');
          productFormData.append('HowToClaim', formData.howToClaim || '');
          if (selectedPdfFile) {
            productFormData.append('policyDocumentFile', selectedPdfFile);
          }
          if (selectedHospitals?.length > 0) {
            selectedHospitals.forEach(id => {
              productFormData.append('HospitalIds', id.toString());
            });
          }

          const productResponse = await fetch(`${API_BASE}/products/update-product-plan`, {
            method: 'POST',
            headers: authHeaders,
            body: productFormData
          });
          const productResult = await productResponse.json();
          if (!(productResult?.success === true || productResult?.status === 'success')) {
            throw new Error(productResult?.message || 'Failed to update product');
          }
        }

        // 2. Build the features payload to match HmoPlanServiceFeatureRequest C# Model
        const features = [];
        selectedPlanServices.forEach(planServiceId => {
          const descriptions = planDescriptionServicesMap[planServiceId] || [];
          const selectedDescriptions = descriptions.filter(d => d.selected);
          
          selectedDescriptions.forEach(desc => {
            const psId = parseInt(planServiceId, 10);
            const psdId = parseInt(desc.id, 10);
            
            // ✅ Ensure we only push valid numeric IDs to prevent C# model binding errors
            if (!isNaN(psId) && !isNaN(psdId) && psId > 0 && psdId > 0) {
              features.push({
                PlanServiceId: psId,
                PlanServiceDescriptionId: psdId,
                Naration: desc.narrator || '',
                // ✅ Clean amount string (remove commas) before parsing to prevent NaN
                Amounts: parseFloat(String(desc.amounts).replace(/,/g, '')) || 0,
                IsActive: true,
                FamilyType: desc.familyType || null,
                NumberOfFamilyMembers: desc.numberOfFamilyMembers ? parseInt(desc.numberOfFamilyMembers, 10) : null
              });
            }
          });
        });

        if (features.length === 0) {
          throw new Error('At least one Plan Service must be selected, and at least one Description Service must be checked with valid details.');
        }

        // ✅ SAFEGUARD: Ensure hmoPlanId is a valid integer before sending
        const hmoPlanIdNum = parseInt(hmoPlanId, 10);
        if (isNaN(hmoPlanIdNum) || hmoPlanIdNum <= 0) {
          throw new Error(`Invalid HMO Plan ID (${hmoPlanId}). Product creation/update may have failed or returned an unexpected format.`);
        }

        const payload = {
          HmoPlanId: hmoPlanIdNum,
          Features: features
        };

        console.log('🚀 [DEBUG] Sending HMO Features Payload:', JSON.stringify(payload, null, 2)); // For debugging

        // 3. Save the features using the NEW upsert endpoint
        try {
          const response = await authenticatedFetch(
            `${API_BASE}/Hmoplandescriptionservice/upsert-hmo-plan-service-features`,
            'POST',
            payload
          );
          
          if (response?.success === true || response?.status === 'success') {
            toast.success(editingItem ? 'HMO Plan features updated successfully' : 'HMO Plan created successfully');
            handleCloseModal();
            await loadData();
          } else {
            console.error('❌ [DEBUG] HMO Features API Error Response:', response);
            throw new Error(response?.message || response?.details || 'Failed to save features. Check console for details.');
          }
        } catch (error) {
          console.error('❌ [DEBUG] HMO Features Save Error:', error);
          throw new Error(error.message || 'An unexpected error occurred while saving features.');
        }
      }
      // ✅ Admins also use FormData
      else if (entity === 'admins' && !editingItem && config.usesFormSubmit) {
        const formDataObj = new FormData();
        formDataObj.append('Name', formData.name || '');
        formDataObj.append('Email', formData.email || '');
        formDataObj.append('RoleId', formData.roleId || '');
        if (formData.password) formDataObj.append('Password', formData.password);

        const response = await fetch(config.createEndpoint, {
          method: 'POST',
          headers: authHeaders,
          body: formDataObj
        });
        const result = await response.json();
        const resultMsg = result?.message?.toLowerCase() || '';

        if (resultMsg.includes('invalid token') || resultMsg.includes('invalid secret') || resultMsg.includes('authorization')) {
          toast.error('Session expired. Please log in again.');
          clearStoredCredentials();
          navigate('/admin-login', { replace: true });
          return;
        }

        if (result?.success === true || result?.status === 'success') {
          await loadData();
          handleCloseModal();
          toast.success('Admin created successfully');
        } else {
          throw new Error(result.message || 'Request failed');
        }
      }
      // ✅ All other entities use JSON via authenticatedFetch
      else {
        const primaryFieldName = getPrimaryFieldName();
        let payload = {
          id: editingItem?.id,
          [primaryFieldName]: formData[primaryFieldName],
          name: formData.name || formData.providerName || formData.source,
          source: formData.source,
          description: formData.description,
          category: formData.category,
          location: formData.location,
          contactPhone: formData.contactPhone,
          contactEmail: formData.contactEmail,
          code: formData.code,
          isActive: formData.isActive !== false,
          stateName: formData.stateName,
          address: formData.address,
          providerName: formData.providerName,
          contactPerson: formData.contactPerson,
          contactNumber: formData.contactNumber,
          email: formData.email,
          password: !editingItem ? formData.password : undefined,
          NewPassword: editingItem ? formData.password : undefined,
          CategoryId: formData.categoryId,
          PlanCategoryId: formData.planCategoryId,
          ProviderId: formData.providerId,
          MinimumPremium: formData.minimumPremium,
          FamilyPremium: formData.familyPremium,
          Status: formData.status,
          Benefits: formData.benefits,
          HowItWorks: formData.howItWorks,
          HowToClaim: formData.howToClaim,
          RoleId: formData.roleId,
          HospitalIds: entity === 'products' ? selectedHospitals : undefined
        };

        if (entity === 'plan-categories') {
          payload = {
            categoryName: formData.categoryName
          };
          if (editingItem) {
            payload.planCategoryUid = editingItem.planCategoryUid;
          }
        }
        if (entity === 'planservices') {
          payload = {
            Id: editingItem?.id,
            ServiceName: formData.servicename,
            PlanId: null,
            CompanyId: 1,
            IsActive: true
          };
        }
        if (entity === 'plandescriptionservices') {
          payload = {
            Id: editingItem?.id,
            DescriptionName: formData.descriptionName,
            IsActive: true
          };
        }

        Object.keys(payload).forEach(key => {
          if (payload[key] === undefined || payload[key] === null || payload[key] === '')
            delete payload[key];
        });

        const endpoint = editingItem ? config.updateEndpoint : config.createEndpoint;
        const method = editingItem ? 'PUT' : 'POST';
        const response = await authenticatedFetch(endpoint, method, payload);
        const resultMsg = response?.message?.toLowerCase() || '';

        if (resultMsg.includes('invalid token') || resultMsg.includes('invalid secret') || resultMsg.includes('authorization')) {
          toast.error('Session expired. Please log in again.');
          clearStoredCredentials();
          navigate('/admin-login', { replace: true });
          return;
        }

        if (response?.success === true || response?.status === 'success') {
          if (editingItem) {
            setData(prev => prev.map(item =>
              item.id === editingItem.id ? { ...item, ...formData } : item
            ));
            toast.success('Updated successfully');
          } else {
            await loadData();
            toast.success('Created successfully');
          }
          handleCloseModal();
          setSelectedHospitals([]);
          setSelectedStateForHospitals('');
          setSelectedPdfFile(null);
        } else {
          throw new Error(response.message || 'Request failed');
        }
      }
    } catch (error) {
      console.error('❌ Submit error:', error);
      const errMsg = error.message?.toLowerCase() || '';
      if (errMsg.includes('invalid token') || errMsg.includes('invalid secret') || errMsg.includes('authorization')) {
        toast.error('Authentication failed. Please log in again.');
        clearStoredCredentials();
        navigate('/admin-login', { replace: true });
        return;
      }
      toast.error(error.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = (e) => {
    e?.preventDefault();
    clearStoredCredentials();
    toast.success('Logged out successfully');
    navigate('/admin-login', { replace: true });
  };

  const handleRefresh = () => loadData();

  const handleClearFilters = () => {
    setStateFilter(null);
    setGlobalFilter('');
    loadData();
  };

  // ✅ Action Buttons
  const actionBodyTemplate = (rowData) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-button-success p-button-sm"
        onClick={() => handleEdit(rowData)}
        tooltip="Edit"
        data-id={rowData.id}
        style={{ backgroundColor: BRAND_ORANGE, borderColor: BRAND_ORANGE }}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-danger p-button-sm"
        onClick={(e) => handleDelete(e, rowData)}
        tooltip="Delete"
        ref={deleteRef}
        data-id={rowData.id}
      />
    </div>
  );

  const statusBodyTemplate = (rowData) => (
    <Tag value={rowData.isActive !== false ? 'Active' : 'Inactive'} severity={rowData.isActive !== false ? 'success' : 'danger'} className="fw-semibold" />
  );

  const productStatusBodyTemplate = (rowData) => {
    const getStatusSeverity = (status) => {
      switch (status) {
        case 'Active': return 'success';
        case 'Inactive': return 'danger';
        case 'Draft': return 'warning';
        case 'Archived': return 'info';
        default: return 'secondary';
      }
    };
    return <Tag value={rowData.status || 'Unknown'} severity={getStatusSeverity(rowData.status)} className="fw-semibold" />;
  };

  const navItems = [
    { section: 'MAIN', items: [{ name: 'Dashboard', icon: 'fa-chart-line', path: '/hmoadmin', active: location.pathname === '/hmoadmin' }] },
    {
      section: 'MANAGEMENT', items: [
        { name: 'Clients', icon: 'fa-building', path: '/admin/companies', active: location.pathname.startsWith('/admin/companies') },
        { name: 'Documents', icon: 'fa-file-contract', path: '/admin/company-documents', active: location.pathname.startsWith('/admin/company-documents') }
      ]
    },
    {
      section: 'Settings', items: [
        { name: 'States', icon: 'fa-map-marker-alt', path: '/admin/crud/states', active: entity === 'states' },
        { name: 'Titles', icon: 'fa-certificate', path: '/admin/crud/titles', active: entity === 'titles' },
        { name: 'Agent Types', icon: 'fa-user-tag', path: '/admin/crud/agent-types', active: entity === 'agent-types' },
        { name: 'Hear About', icon: 'fa-bullhorn', path: '/admin/crud/hear-about', active: entity === 'hear-about' },
        { name: 'Business Categories', icon: 'fa-briefcase', path: '/admin/crud/business-categories', active: entity === 'business-categories' },
        { name: 'Business Sectors', icon: 'fa-industry', path: '/admin/crud/business-sectors', active: entity === 'business-sectors' },
        { name: 'Products of Interest', icon: 'fa-box', path: '/admin/crud/products-of-interest', active: entity === 'products-of-interest' },
        { name: 'Hospitals', icon: 'fa-hospital', path: '/admin/crud/hospitals', active: entity === 'hospitals' },
        { name: 'Product Categories', icon: 'fa-folder-open', path: '/admin/crud/product-categories', active: entity === 'product-categories' },
        { name: 'Plan Categories', icon: 'fa-tags', path: '/admin/crud/plan-categories', active: entity === 'plan-categories' },
        { name: 'Plan Services', icon: 'fa-concierge-bell', path: '/admin/crud/planservices', active: entity === 'planservices' },
        { name: 'Plan Description Services', icon: 'fa-concierge-bell', path: '/admin/crud/plandescriptionservices', active: entity === 'plandescriptionservices' },
        { name: 'Providers', icon: 'fa-building', path: '/admin/crud/providers', active: entity === 'providers' },
        { name: 'Plan', icon: 'fa-box-open', path: '/admin/crud/products', active: entity === 'products' },
        { name: 'Hmo Plans & features', icon: 'fa-box-open', path: '/admin/crud/plansandfeatures', active: entity === 'plansandfeatures' },
        { name: 'Admin Users', icon: 'fa-user-shield', path: '/admin/crud/admins', active: entity === 'admins' },
        { name: 'Permissions', icon: 'fa-shield-alt', path: '/admin/crud/permissions', active: entity === 'permissions' },
        { name: 'Roles', icon: 'fa-user-tag', path: '/admin/crud/roles', active: entity === 'roles' }
      ]
    }
  ];

  const getFieldName = () => config.formFields[0]?.name || getPrimaryFieldName();
  const getFieldLabel = () => config.formFields[0]?.label || 'Name';

  if (loading && data.length === 0) {
    return (
      <div className="admin-dashboard-page d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-orange mb-3" role="status" style={{ width: '3rem', height: '3rem' }}><span className="visually-hidden">Loading...</span></div>
          <p className="text-muted">Loading {config.title}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      <ConfirmPopup />

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/admin" className="brand-logo">
            <img src="/images/fsl_logo.png" alt="Logo" style={{ height: '45px' }} />
          </Link>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((section, sectionIdx) => (
            <div key={sectionIdx} className="nav-section">
              <h6 className="nav-section-title">{section.section}</h6>
              <ul className="nav-list">
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx} className="nav-item">
                    {item.hasDropdown ? (
                      <>
                        <button className={`nav-link ${item.active ? 'active' : ''} ${isExpanded(item.path) ? 'expanded' : ''}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDropdown(item.path); }} type="button" aria-expanded={isExpanded(item.path)}>
                          <i className={`fas ${item.icon} me-2`}></i><span>{item.name}</span>
                          <i className={`fas fa-chevron-down ms-auto dropdown-toggle ${isExpanded(item.path) ? 'rotated' : ''}`}></i>
                        </button>
                        <div className={`dropdown-menu ${isExpanded(item.path) ? 'show' : ''}`}>
                          <ul className="dropdown-list">
                            {item.children?.map((child, childIdx) => (
                              <li key={childIdx}>
                                <Link to={child.path} className={`dropdown-item ${child.active ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                                  <i className={`fas ${child.icon} me-2`}></i><span>{child.name}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </>
                    ) : (
                      <Link to={item.path} className={`nav-link ${item.active ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <i className={`fas ${item.icon} me-2`}></i><span>{item.name}</span>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="nav-section">
            <h6 className="nav-section-title">ACCOUNT</h6>
            <ul className="nav-list">
              <li><a href="/" className="nav-link" onClick={handleLogout}><i className="fas fa-sign-out-alt me-2"></i>Logout</a></li>
            </ul>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        <header className="top-header">
          <div className="header-left">
            <button className="sidebar-toggle d-lg-none" onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}><i className="fas fa-bars"></i></button>
            <div className="page-title"><h4 className="mb-0" style={{ color: '#1f2937', fontWeight: '600' }}>{config.title}</h4></div>
          </div>
          <div className="header-right">
            <button className="header-icon"><i className="fas fa-search"></i></button>
            <button className="header-icon"><i className="fas fa-bell"></i><span className="badge">2</span></button>
            <div className="user-avatar" style={{ width: '40px', height: '40px', borderRadius: '50%', background: `linear-gradient(135deg, ${BRAND_ORANGE} 0%, ${BRAND_ORANGE_DARK} 100%)`, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', cursor: 'pointer' }}>AD</div>
          </div>
        </header>

        <div className="content-wrapper" style={{ padding: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
            {
              config.title !== 'Hmo Plans & Features' && (
                <div>
                  <button className="btn" onClick={handleCreate} style={{ backgroundColor: BRAND_ORANGE, color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}><i className="fas fa-plus"></i>
                    {config.title.slice(0, -1)}
                  </button>
                  <h5 className="mb-1">Manage {config.title}</h5><p className="text-muted small mb-0">{filteredData.length} {config.title.toLowerCase()}</p>
                </div>
              )
            }
          </div>

          {entity === 'hospitals' && (
            <Card className="mb-3 border-0 shadow-sm" data-aos="fade-up">
              <div className="card-body py-3">
                <div className="row g-3 align-items-end">
                  <div className="col-md-8 m-2 p-2">
                    <label className="form-label small fw-semibold text-muted mb-1">Filter by State</label>
                    <BootstrapSelect
                      value={stateFilter}
                      onChange={(e) => { setStateFilter(e.target.value || null); setTimeout(() => loadData(), 0); }}
                      options={stateOptions}
                      placeholder="All States"
                      className="w-100"
                      showClear
                    />
                  </div>
                  <div className="col-md-4 m-2 p-2">
                    <Button label="Clear Filters" icon="pi pi-filter-slash" className="p-button-outlined w-100" onClick={handleClearFilters} disabled={!stateFilter && !globalFilter} style={{ borderColor: BRAND_ORANGE, color: BRAND_ORANGE }} />
                  </div>
                </div>
                {stateFilter && (
                  <div className="mt-2 d-flex gap-2 flex-wrap">
                    <Tag value={`State: ${stateFilter}`} severity="info" onRemove={() => { setStateFilter(null); loadData(); }} removable />
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Search Bar */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: '300px' }}>
              <i className="fas fa-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}></i>
              <input
                type="text"
                placeholder={`Search ${config.title.toLowerCase()}...`}
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                style={{ paddingLeft: '40px', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', width: '100%', outline: 'none' }}
              />
            </div>
            <button className="btn" style={{ border: '1px solid #d1d5db', padding: '10px 16px', borderRadius: '8px', color: '#6b7280', background: 'white', cursor: 'pointer' }}>
              <i className="fas fa-filter me-2"></i>Filter
            </button>
          </div>

          {/* DataTable */}
          <Card className="border-0 shadow-sm p-5" data-aos="fade-up" data-aos-delay="100">
            <div className="card-body p-0">
              <DataTable
                value={filteredData}
                paginator
                showGridlines
                rows={10}
                rowsPerPageOptions={[5, 10, 25, 50]}
                tableStyle={{ minWidth: '1000px', marginRight: '1rem', marginTop: '1rem' }}
                emptyMessage={`No ${config.title.toLowerCase()} found`}
                className="p-datatable-sm"
                dataKey="id"
                responsiveLayout="scroll"
              >
                {config.columns.map((col, idx) => (
                  <Column
                    key={idx}
                    field={col.field}
                    header={col.header}
                    sortable={col.sortable}
                    style={col.style}
                    body={col.body || (entity === 'products' && col.field === 'status' ? productStatusBodyTemplate : entity === 'admins' && col.field === 'isActive' ? statusBodyTemplate : undefined)}
                  />
                ))}
                {config.hasStatus && entity !== 'products' && entity !== 'admins' && (
                  <Column field="isActive" header="Status" body={statusBodyTemplate} style={{ width: '100px' }} />
                )}
                <Column header="Actions" body={actionBodyTemplate} style={{ width: '150px' }} exportable={false} />
              </DataTable>
            </div>
          </Card>
        </div>
      </div>

      {/* Modal */}
      {modalVisible && (
        <div className="modal-overlay" onClick={handleCloseModal} style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          background: 'rgba(0,0,0,0.5)', zIndex: 1050,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'fadeIn 0.3s ease'
        }}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{
            background: 'white', borderRadius: '12px', width: '95%',
            maxWidth: '1000px', maxHeight: '90vh', overflowY: 'auto',
            animation: 'slideIn 0.3s ease',
            position: 'relative',
            zIndex: 1051
          }}>
            <div className="modal-header" style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5 style={{ margin: 0, color: '#1f2937', fontWeight: '600' }}>{editingItem ? `Edit ${config.title.slice(0, -1)}` : `Add New ${config.title.slice(0, -1)}`}</h5>
              <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: 'pointer', padding: '0', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}>×</button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
              {/* Main Name Field */}
              <div style={{ marginBottom: '20px' }}>
                <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>{getFieldLabel()} <span style={{ color: '#ef4444' }}>*</span></label>
                <InputText name={getFieldName()} value={formData[getFieldName()] || ''} onChange={handleInputChange} className={`w-100 ${formErrors[getFieldName()] ? 'is-invalid' : ''}`} placeholder={`Enter ${getFieldLabel().toLowerCase()}`} disabled={submitting} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px' }} />
                {formErrors[getFieldName()] && (<small className="text-danger" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>{formErrors[getFieldName()]}</small>)}
              </div>

              {/* 🚀 Plan Services & Descriptions for HMO Plans & Features */}
              {entity === 'plansandfeatures' && (
                <div style={{ marginBottom: '20px', borderTop: '2px solid #e5e7eb', paddingTop: '20px' }}>
                  <h6 style={{ marginBottom: '15px', fontWeight: '600', color: '#374151' }}>
                    <i className="fas fa-concierge-bell me-2" style={{ color: BRAND_ORANGE }}></i>
                    Plan Services & Descriptions
                  </h6>
                  {formErrors.planServices && (
                    <small className="text-danger d-block mb-2" style={{ fontSize: '13px' }}>
                      <i className="fas fa-exclamation-circle me-1"></i> {formErrors.planServices}
                    </small>
                  )}
                  {servicesLoading ? (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-orange" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <small className="text-muted d-block mt-2">Loading services...</small>
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <label className="form-label small fw-semibold">Add Plan Service</label>
                        <select
                          className="form-select"
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              handlePlanServiceSelect(e.target.value);
                              e.target.value = "";
                            }
                          }}
                          disabled={servicesLoading}
                        >
                          <option value="">+ Select a Plan Service to add</option>
                          {planServicesList
                            .filter(s => !selectedPlanServices.some(id => String(id) === String(s.id)))
                            .map(s => (
                              <option key={s.id} value={s.id}>{s.serviceName}</option>
                            ))}
                        </select>
                      </div>

                      {selectedPlanServices.length > 0 && (
                        <Accordion multiple activeIndex={selectedPlanServices.map((_, i) => i)}>
                          {selectedPlanServices.map((planServiceId, index) => {
                            const planService = planServicesList.find(s => String(s.id) === String(planServiceId));
                            const descriptions = planDescriptionServicesMap[planServiceId] || [];
                            const existingRecords = existingPlanServices.filter(
                              item => {
                                const itemServiceId = item.planServiceId || item.planserviceid || item.PlanServiceId;
                                return String(itemServiceId) === String(planServiceId);
                              }
                            );

                            return (
                              <AccordionTab
                                key={planServiceId}
                                header={
                                  <div className="d-flex align-items-center justify-content-between w-100">
                                    <span>
                                      <i className="fas fa-concierge-bell me-2" style={{ color: BRAND_ORANGE }}></i>
                                      {planService?.serviceName ? `${planService.serviceName}` : `Service ${index + 1}`}
                                      {existingRecords.length > 0 && (
                                        <span className="badge bg-success ms-2">
                                          {existingRecords.length} existing
                                        </span>
                                      )}
                                    </span>
                                    <Button
                                      icon="pi pi-trash"
                                      className="p-button-rounded p-button-danger p-button-sm p-button-text"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemovePlanService(planServiceId);
                                      }}
                                      tooltip="Remove Service"
                                    />
                                  </div>
                                }
                              >
                                {descriptions.length === 0 ? (
                                  <div className="alert alert-info mb-0 small">
                                    <i className="fas fa-info-circle me-1"></i>
                                    No description services available for this plan service.
                                  </div>
                                ) : (
                                  <div style={{ display: 'grid', gap: '12px' }}>
                                    {descriptions.map((desc) => {
                                      const isExisting = existingRecords.some(
                                        existing => {
                                          const existingDescId = existing.planServiceDescriptionId || existing.planservicedescriptionid || existing.PlanServiceDescriptionId;
                                          return String(existingDescId) === String(desc.id);
                                        }
                                      );

                                      return (
                                        <div
                                          key={desc.id}
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            padding: '12px',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            backgroundColor: isExisting ? '#d1fae5' : desc.selected ? '#fff7ed' : 'white',
                                            transition: 'all 0.2s ease',
                                            flexWrap: 'wrap'
                                          }}
                                        >
                                          <div className="form-check" style={{ minWidth: '24px', margin: 0 }}>
                                            <input
                                              type="checkbox"
                                              className="form-check-input"
                                              checked={desc.selected || isExisting}
                                              onChange={(e) => handleDescriptionServiceToggle(planServiceId, desc.id, e.target.checked)}
                                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                              id={`desc-${planServiceId}-${desc.id}`}
                                            />
                                          </div>
                                          <div style={{ flex: 1, minWidth: '150px' }}>
                                            <label
                                              htmlFor={`desc-${planServiceId}-${desc.id}`}
                                              style={{ fontWeight: '500', color: '#374151', cursor: 'pointer', margin: 0 }}
                                            >
                                              {desc.descriptionName}
                                              {isExisting && (
                                                <span className="badge bg-success ms-2">Existing</span>
                                              )}
                                            </label>
                                          </div>

                                          {(desc.selected || isExisting) && (
                                            <div style={{
                                              display: 'grid',
                                              gridTemplateColumns: '1fr 1fr 1fr 1fr',
                                              gap: '12px',
                                              width: '100%',
                                              marginTop: '8px',
                                              padding: '12px',
                                              backgroundColor: '#f8fafc',
                                              borderRadius: '8px',
                                              border: '1px solid #e2e8f0'
                                            }}>
                                              <div>
                                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '4px' }}>Narration</label>
                                                <InputText
                                                  value={desc.narrator || ''}
                                                  onChange={(e) => handleDescriptionNarratorChange(planServiceId, desc.id, e.target.value)}
                                                  placeholder="e.g., Consultation"
                                                  style={{ width: '100%' }}
                                                  className="form-control form-control-sm"
                                                />
                                              </div>
                                              <div>
                                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '4px' }}>Family Type</label>
                                                <select
                                                  className="form-select form-select-sm"
                                                  value={desc.familyType || ''}
                                                  onChange={(e) => {
                                                    setPlanDescriptionServicesMap(prev => ({
                                                      ...prev,
                                                      [planServiceId]: prev[planServiceId].map(d =>
                                                        d.id === desc.id ? { ...d, familyType: e.target.value } : d
                                                      )
                                                    }));
                                                  }}
                                                >
                                                  <option value="">Select Type</option>
                                                  <option value="Individual">Individual</option>
                                                  <option value="Family">Family</option>
                                                  <option value="SME">SME</option>
                                                  <option value="66 – 70 Yrs Old Premium (N)">66 – 70 Yrs Old Premium (N)</option>
                                                  <option value="71 – 75 Yrs Old Premium (N)">71 – 75 Yrs Old Premium (N)</option>
                                                  <option value="76 Yrs and Above">76 Yrs and Above</option>
                                                  <option value="Minimum of 25 Lives 6 Members">Minimum of 25 Lives 6 Members</option>
                                                   <option value="Minimum of 25 Lives 6 Members">Minimum of 25 Lives 6 Members</option>
                                                  
                                                </select>
                                              </div>
                                              <div>
                                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '4px' }}>No. of Members</label>
                                                <InputNumber
                                                  value={desc.numberOfFamilyMembers || null}
                                                  onValueChange={(e) => {
                                                    setPlanDescriptionServicesMap(prev => ({
                                                      ...prev,
                                                      [planServiceId]: prev[planServiceId].map(d =>
                                                        d.id === desc.id ? { ...d, numberOfFamilyMembers: e.value } : d
                                                      )
                                                    }));
                                                  }}
                                                  placeholder="e.g., 4"
                                                  style={{ width: '100%' }}
                                                  className="form-control form-control-sm"
                                                  min={1}
                                                />
                                              </div>
                                              <div>
                                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '4px' }}>Amount (₦)</label>
                                                <InputNumber
                                                  value={desc.amounts || 0}
                                                  onValueChange={(e) => {
                                                    setPlanDescriptionServicesMap(prev => ({
                                                      ...prev,
                                                      [planServiceId]: prev[planServiceId].map(d =>
                                                        d.id === desc.id ? { ...d, amounts: e.value } : d
                                                      )
                                                    }));
                                                  }}
                                                  mode="currency"
                                                  currency="NGN"
                                                  locale="en-NG"
                                                  style={{ width: '100%' }}
                                                  className="form-control form-control-sm"
                                                />
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </AccordionTab>
                            );
                          })}
                        </Accordion>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Admin Users specific fields */}
              {entity === 'admins' && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Email Address <span style={{ color: '#ef4444' }}>*</span></label>
                    <InputText name="email" type="email" value={formData.email || ''} onChange={handleInputChange} className={`w-100 ${formErrors.email ? 'is-invalid' : ''}`} placeholder="e.g., john@company.com" disabled={submitting} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px' }} />
                    {formErrors.email && <small className="text-danger" style={{ fontSize: '12px' }}>{formErrors.email}</small>}
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Role <span style={{ color: '#ef4444' }}>*</span></label>
                    <BootstrapSelect
                      name="roleId"
                      value={formData.roleId ?? null}
                      onChange={(e) => setFormData(prev => ({ ...prev, roleId: e.target.value }))}
                      options={roleOptions}
                      placeholder={roleOptions.length > 0 ? "Select role" : "Loading roles..."}
                      className={formErrors.roleId ? 'is-invalid' : ''}
                      disabled={submitting || roleOptions.length === 0}
                    />
                    {formErrors.roleId && <small className="text-danger" style={{ fontSize: '12px' }}>{formErrors.roleId}</small>}
                  </div>
                  {!editingItem && (
                    <div style={{ marginBottom: '20px' }}>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Password <span style={{ color: '#6b7280', fontWeight: '400' }}>(Optional)</span></label>
                      <InputText name="password" type="password" value={formData.password || ''} onChange={handleInputChange} className="w-100" placeholder="Enter password or leave blank" disabled={submitting} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px' }} />
                    </div>
                  )}
                  {editingItem && (
                    <div style={{ marginBottom: '20px' }}>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>New Password <span style={{ color: '#6b7280', fontWeight: '400' }}>(Optional)</span></label>
                      <InputText name="password" type="password" value={formData.password || ''} onChange={handleInputChange} className="w-100" placeholder="Enter new password" disabled={submitting} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px' }} />
                    </div>
                  )}
                  <div style={{ marginBottom: '20px' }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Status</label>
                    <BootstrapSelect
                      name="isActive"
                      value={formData.isActive !== false ? 'Active' : 'Inactive'}
                      onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === 'Active' }))}
                      options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                      disabled={submitting}
                    />
                  </div>
                </>
              )}

              {/* Products & HMO Plans specific fields */}
              {(entity === 'products' || entity === 'plansandfeatures') && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Category <span style={{ color: '#ef4444' }}>*</span></label>
                      <BootstrapSelect
                        name="categoryId"
                        value={formData.categoryId ?? null}
                        onChange={(e) => setFormData(prev => ({ ...prev, categoryId: e.target.value }))}
                        options={categoryOptions}
                        placeholder={categoryOptions.length > 0 ? "Select category" : "Loading..."}
                        className={`w-100 ${formErrors.categoryId ? 'is-invalid' : ''}`}
                        disabled={submitting || categoryOptions.length === 0}
                      />
                      {formErrors.categoryId && <small className="text-danger" style={{ fontSize: '12px' }}>{formErrors.categoryId}</small>}
                    </div>
                    <div>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Plan Category <span style={{ color: '#ef4444' }}>*</span></label>
                      <BootstrapSelect
                        name="planCategoryId"
                        value={formData.planCategoryId ?? null}
                        onChange={(e) => setFormData(prev => ({ ...prev, planCategoryId: e.target.value }))}
                        options={planCategoryOptions}
                        placeholder={planCategoryOptions.length > 0 ? "Select plan category" : "Loading..."}
                        className={`w-100 ${formErrors.planCategoryId ? 'is-invalid' : ''}`}
                        disabled={submitting || planCategoryOptions.length === 0}
                      />
                      {formErrors.planCategoryId && <small className="text-danger" style={{ fontSize: '12px' }}>{formErrors.planCategoryId}</small>}
                    </div>
                    <div>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Provider <span style={{ color: '#ef4444' }}>*</span></label>
                      <BootstrapSelect
                        name="providerId"
                        value={formData.providerId ?? null}
                        onChange={(e) => setFormData(prev => ({ ...prev, providerId: e.target.value }))}
                        options={providerOptions}
                        placeholder={providerOptions.length > 0 ? "Select provider" : "Loading..."}
                        className={`w-100 ${formErrors.providerId ? 'is-invalid' : ''}`}
                        disabled={submitting || providerOptions.length === 0}
                      />
                      {formErrors.providerId && <small className="text-danger" style={{ fontSize: '12px' }}>{formErrors.providerId}</small>}
                    </div>
                  </div>

                  {/* Hospital Selection Section */}
                  <div style={{ marginBottom: '20px', padding: '16px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <h6 style={{ marginBottom: '12px', fontWeight: '600', color: '#374151' }}><i className="fas fa-hospital me-2" style={{ color: BRAND_ORANGE }}></i>Associated Hospitals</h6>
                    <div style={{ marginBottom: '12px' }}>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px', color: '#6b7280' }}>Filter Hospitals by State</label>
                      <BootstrapSelect
                        value={selectedStateForHospitals}
                        onChange={(e) => {
                          const stateValue = e.target.value;
                          setSelectedStateForHospitals(stateValue);
                          if (stateValue && typeof stateValue === 'string') {
                            loadHospitalsByState(stateValue);
                          } else {
                            setHospitalOptions([]);
                            setSelectedHospitals([]);
                          }
                        }}
                        options={stateOptions.filter(s => s.value && typeof s.value === 'string')}
                        placeholder="Select state to filter hospitals"
                      />
                    </div>
                    <div>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px', color: '#6b7280' }}>Select Hospitals <span style={{ color: '#6b7280', fontWeight: '400' }}>(Optional)</span></label>
                      <BootstrapMultiSelect
                        value={selectedHospitals}
                        onChange={(e) => setSelectedHospitals(e.target.value)}
                        options={hospitalOptions}
                        placeholder={hospitalOptions.length > 0 ? "Select hospitals (hold Ctrl/Cmd)" : "Select a state to view hospitals"}
                        disabled={hospitalOptions.length === 0}
                      />
                      {hospitalOptions.length === 0 && (
                        <small className="text-muted" style={{ fontSize: '12px' }}>
                          {selectedStateForHospitals ? 'No hospitals found in this state' : 'Select a state to view hospitals'}
                        </small>
                      )}
                    </div>
                    {selectedHospitals.length > 0 && (
                      <div style={{ marginTop: '12px' }}>
                        <small className="text-muted" style={{ fontSize: '12px' }}>{selectedHospitals.length} hospital(s) selected</small>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                          {selectedHospitals.map(hospitalId => {
                            const hospital = hospitalOptions.find(h => h.value === hospitalId);
                            if (!hospital) return null;
                            const displayName = hospital.rawName || hospital.label.split(' - ')[0].replace(/^[🏥👁️🏢]\s*/, '');
                            const category = hospital.category || 'N/A';
                            return (
                              <Tag
                                key={hospitalId}
                                value={`${displayName} (${category})`}
                                severity={getCategorySeverity(category)}
                                removable
                                onRemove={() => setSelectedHospitals(selectedHospitals.filter(id => id !== hospitalId))}
                                style={{ fontSize: '12px', fontWeight: '500' }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 3-Column Grid for Premiums and Status */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Minimum Premium (₦) <span style={{ color: '#ef4444' }}>*</span></label>
                      <InputNumber name="minimumPremium" value={formData.minimumPremium || ''} onValueChange={(e) => setFormData(prev => ({ ...prev, minimumPremium: e.value }))} className={`w-100 ${formErrors.minimumPremium ? 'is-invalid' : ''}`} placeholder="e.g., 5000" disabled={submitting} mode="currency" currency="NGN" locale="en-NG" style={{ width: '100%' }} />
                      {formErrors.minimumPremium && <small className="text-danger" style={{ fontSize: '12px' }}>{formErrors.minimumPremium}</small>}
                    </div>
                    <div>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Family Premium (₦)</label>
                      <InputNumber name="familyPremium" value={formData.familyPremium || ''} onValueChange={(e) => setFormData(prev => ({ ...prev, familyPremium: e.value }))} className="w-100" placeholder="e.g., 15000" disabled={submitting} mode="currency" currency="NGN" locale="en-NG" style={{ width: '100%' }} />
                    </div>
                    <div>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Status <span style={{ color: '#ef4444' }}>*</span></label>
                      <BootstrapSelect
                        name="status"
                        value={formData.status ?? null}
                        onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                        options={PRODUCT_STATUS_OPTIONS}
                        placeholder="Select status"
                        className={`w-100 ${formErrors.status ? 'is-invalid' : ''}`}
                        disabled={submitting}
                      />
                      {formErrors.status && <small className="text-danger" style={{ fontSize: '12px' }}>{formErrors.status}</small>}
                    </div>
                  </div>

                  {/* PDF File Upload */}
                  <div style={{ marginBottom: '20px' }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                      Policy Document (PDF) <span style={{ color: '#6b7280', fontWeight: '400' }}>(Max 10MB)</span>
                    </label>
                    {!selectedPdfFile ? (
                      <div
                        style={{
                          border: '2px dashed #d1d5db',
                          borderRadius: '8px',
                          padding: '20px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          backgroundColor: '#f9fafb'
                        }}
                        onClick={() => pdfInputRef.current?.click()}
                        onMouseEnter={(e) => e.currentTarget.style.borderColor = BRAND_ORANGE}
                        onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                      >
                        <input
                          ref={pdfInputRef}
                          type="file"
                          accept=".pdf,application/pdf"
                          onChange={handlePdfFileChange}
                          style={{ display: 'none' }}
                          disabled={submitting}
                        />
                        <i className="fas fa-cloud-upload-alt" style={{ fontSize: '32px', color: BRAND_ORANGE, marginBottom: '10px', display: 'block' }}></i>
                        <p style={{ margin: '5px 0', color: '#374151', fontWeight: '500' }}>Click to upload PDF</p>
                        <p style={{ margin: '5px 0', color: '#6b7280', fontSize: '12px' }}>or drag and drop</p>
                        <p style={{ margin: '5px 0', color: '#9ca3af', fontSize: '11px' }}>PDF will be saved to server folder</p>
                      </div>
                    ) : (
                      <div style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '16px',
                        backgroundColor: '#f0fdf4',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <i className="fas fa-file-pdf" style={{ fontSize: '32px', color: '#ef4444' }}></i>
                          <div>
                            <p style={{ margin: 0, fontWeight: '600', color: '#374151' }}>{selectedPdfFile.name}</p>
                            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>{formatFileSize(selectedPdfFile.size)}</p>
                            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#10b981' }}>✓ Ready to upload</p>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => pdfInputRef.current?.click()}
                            disabled={submitting}
                            style={{
                              padding: '6px 12px',
                              border: 'none',
                              borderRadius: '6px',
                              backgroundColor: BRAND_ORANGE,
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                          >
                            <i className="fas fa-sync-alt me-1"></i>Replace
                          </button>
                          <button
                            type="button"
                            onClick={handleRemovePdf}
                            disabled={submitting}
                            style={{
                              padding: '6px 12px',
                              border: '1px solid #ef4444',
                              borderRadius: '6px',
                              backgroundColor: 'white',
                              color: '#ef4444',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                          >
                            <i className="fas fa-trash me-1"></i>Remove
                          </button>
                        </div>
                      </div>
                    )}
                    {formErrors.policyDocument && (
                      <small className="text-danger" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {formErrors.policyDocument}
                      </small>
                    )}
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Benefits</label>
                    <InputTextarea name="benefits" value={formData.benefits || ''} onChange={handleInputChange} className="w-100" placeholder="List the benefits of this product..." disabled={submitting} rows={3} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }} />
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>How It Works</label>
                    <InputTextarea name="howItWorks" value={formData.howItWorks || ''} onChange={handleInputChange} className="w-100" placeholder="Explain how this product works..." disabled={submitting} rows={3} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }} />
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>How To Claim</label>
                    <InputTextarea name="howToClaim" value={formData.howToClaim || ''} onChange={handleInputChange} className="w-100" placeholder="Explain the claims process..." disabled={submitting} rows={3} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }} />
                  </div>
                </>
              )}

              {/* Hospital-specific fields */}
              {entity === 'hospitals' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>State <span style={{ color: '#ef4444' }}>*</span></label>
                      <BootstrapSelect
                        name="stateName"
                        value={formData.stateName ?? null}
                        onChange={(e) => setFormData(prev => ({ ...prev, stateName: e.target.value }))}
                        options={stateOptions.filter(o => o.value)}
                        placeholder="Select state"
                        className={`w-100 ${formErrors.stateName ? 'is-invalid' : ''}`}
                        disabled={submitting}
                      />
                      {formErrors.stateName && <small className="text-danger" style={{ fontSize: '12px' }}>{formErrors.stateName}</small>}
                    </div>
                    <div>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Category <span style={{ color: '#6b7280', fontWeight: '400' }}>(Optional)</span></label>
                      <BootstrapSelect
                        name="category"
                        value={formData.category ?? null}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        options={HOSPITAL_CATEGORY_OPTIONS}
                        placeholder="Select category"
                        disabled={submitting}
                      />
                    </div>
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Address <span style={{ color: '#ef4444' }}>*</span></label>
                    <InputTextarea name="address" value={formData.address || ''} onChange={handleInputChange} className={`w-100 ${formErrors.address ? 'is-invalid' : ''}`} placeholder="e.g., 123 Health Street, Ikeja" disabled={submitting} rows={3} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Contact Phone</label>
                      <InputText name="contactPhone" value={formData.contactPhone || ''} onChange={handleInputChange} className="w-100" placeholder="e.g., +234 801 234 5678" disabled={submitting} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px' }} />
                    </div>
                    <div>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Contact Email</label>
                      <InputText name="contactEmail" type="email" value={formData.contactEmail || ''} onChange={handleInputChange} className="w-100" placeholder="e.g., info@hospital.com" disabled={submitting} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px' }} />
                    </div>
                  </div>
                </>
              )}

              {/* Provider-specific fields */}
              {entity === 'providers' && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Contact Person <span style={{ color: '#ef4444' }}>*</span></label>
                    <InputText name="contactPerson" value={formData.contactPerson || ''} onChange={handleInputChange} className={`w-100 ${formErrors.contactPerson ? 'is-invalid' : ''}`} placeholder="e.g., John Doe" disabled={submitting} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px' }} />
                    {formErrors.contactPerson && <small className="text-danger" style={{ fontSize: '12px' }}>{formErrors.contactPerson}</small>}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Contact Number <span style={{ color: '#ef4444' }}>*</span></label>
                      <InputText name="contactNumber" value={formData.contactNumber || ''} onChange={handleInputChange} className={`w-100 ${formErrors.contactNumber ? 'is-invalid' : ''}`} placeholder="e.g., +234 801 234 5678" disabled={submitting} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px' }} />
                      {formErrors.contactNumber && <small className="text-danger" style={{ fontSize: '12px' }}>{formErrors.contactNumber}</small>}
                    </div>
                    <div>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Email <span style={{ color: '#ef4444' }}>*</span></label>
                      <InputText name="email" type="email" value={formData.email || ''} onChange={handleInputChange} className={`w-100 ${formErrors.email ? 'is-invalid' : ''}`} placeholder="e.g., info@provider.com" disabled={submitting} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px' }} />
                      {formErrors.email && <small className="text-danger" style={{ fontSize: '12px' }}>{formErrors.email}</small>}
                    </div>
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Address</label>
                    <InputTextarea name="address" value={formData.address || ''} onChange={handleInputChange} className="w-100" placeholder="e.g., 123 Business Street, Lagos" disabled={submitting} rows={2} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }} />
                  </div>
                  {!editingItem && (
                    <div style={{ marginBottom: '20px' }}>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Password <span style={{ color: '#ef4444' }}>*</span></label>
                      <InputText name="password" type="password" value={formData.password || ''} onChange={handleInputChange} className={`w-100 ${formErrors.password ? 'is-invalid' : ''}`} placeholder="Enter password" disabled={submitting} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px' }} />
                      {formErrors.password && <small className="text-danger" style={{ fontSize: '12px' }}>{formErrors.password}</small>}
                    </div>
                  )}
                  {editingItem && (
                    <div style={{ marginBottom: '20px' }}>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>New Password <span style={{ color: '#6b7280', fontWeight: '400' }}>(Optional)</span></label>
                      <InputText name="password" type="password" value={formData.password || ''} onChange={handleInputChange} className="w-100" placeholder="Enter new password" disabled={submitting} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px' }} />
                    </div>
                  )}
                </>
              )}

              {/* Product Category-specific fields */}
              {entity === 'product-categories' && (
                <div style={{ marginBottom: '20px' }}>
                  <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Category Code <span style={{ color: '#6b7280', fontWeight: '400' }}>(Optional)</span></label>
                  <InputText name="code" value={formData.code || ''} onChange={handleInputChange} className="w-100" placeholder="e.g., HEALTH, LIFE" disabled={submitting} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px', textTransform: 'uppercase' }} />
                </div>
              )}

              {/* ✅ Plan Category-specific fields */}
              {entity === 'plan-categories' && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                      Require Registration <span style={{ color: '#6b7280', fontWeight: '400' }}>(Optional)</span>
                    </label>
                    <BootstrapSelect
                      name="isRequireRegister"
                      value={formData.isRequireRegister !== false ? 'Yes' : 'No'}
                      onChange={(e) => setFormData(prev => ({ ...prev, isRequireRegister: e.target.value === 'Yes' }))}
                      options={[{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }]}
                      disabled={submitting}
                    />
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                      Category Image <span style={{ color: '#6b7280', fontWeight: '400' }}>(Optional)</span>
                    </label>
                    {!selectedImageFile && !formData.image ? (
                      <div
                        style={{
                          border: '2px dashed #d1d5db',
                          borderRadius: '8px',
                          padding: '20px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          backgroundColor: '#f9fafb'
                        }}
                        onClick={() => imageInputRef.current?.click()}
                        onMouseEnter={(e) => e.currentTarget.style.borderColor = BRAND_ORANGE}
                        onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                      >
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageFileChange}
                          style={{ display: 'none' }}
                          disabled={submitting}
                        />
                        <i className="fas fa-cloud-upload-alt" style={{ fontSize: '32px', color: BRAND_ORANGE, marginBottom: '10px', display: 'block' }}></i>
                        <p style={{ margin: '5px 0', color: '#374151', fontWeight: '500' }}>Click to upload Image</p>
                        <p style={{ margin: '5px 0', color: '#6b7280', fontSize: '12px' }}>or drag and drop</p>
                      </div>
                    ) : (
                      <div style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '16px',
                        backgroundColor: '#f0fdf4',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {selectedImageFile ? (
                            <>
                              <i className="fas fa-image" style={{ fontSize: '32px', color: '#10b981' }}></i>
                              <div>
                                <p style={{ margin: 0, fontWeight: '600', color: '#374151' }}>{selectedImageFile.name}</p>
                                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>{formatFileSize(selectedImageFile.size)}</p>
                                <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#10b981' }}>✓ Ready to upload</p>
                              </div>
                            </>
                          ) : (
                            <>
                              <i className="fas fa-image" style={{ fontSize: '32px', color: '#3b82f6' }}></i>
                              <div>
                                <p style={{ margin: 0, fontWeight: '600', color: '#374151' }}>Current Image</p>
                                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                                  <a href={formData.image} target="_blank" rel="noopener noreferrer" style={{ color: BRAND_ORANGE }}>View Image</a>
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => imageInputRef.current?.click()}
                            disabled={submitting}
                            style={{
                              padding: '6px 12px',
                              border: 'none',
                              borderRadius: '6px',
                              backgroundColor: BRAND_ORANGE,
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                          >
                            <i className="fas fa-sync-alt me-1"></i>Replace
                          </button>
                          <button
                            type="button"
                            onClick={handleRemoveImage}
                            disabled={submitting}
                            style={{
                              padding: '6px 12px',
                              border: '1px solid #ef4444',
                              borderRadius: '6px',
                              backgroundColor: 'white',
                              color: '#ef4444',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                          >
                            <i className="fas fa-trash me-1"></i>Remove
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Description Field */}
              {config.hasDescription && (
                <div style={{ marginBottom: '20px' }}>
                  <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Description <span style={{ color: '#6b7280', fontWeight: '400' }}>(Optional)</span></label>
                  <InputTextarea name="description" value={formData.description || ''} onChange={handleInputChange} className="w-100" placeholder="Brief description..." disabled={submitting} rows={3} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }} />
                </div>
              )}

              {/* Two Column Layout for Category/Status */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                {config.hasCategory && entity !== 'hospitals' && (
                  <>
                    <div>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Category <span style={{ color: '#6b7280', fontWeight: '400' }}>(Optional)</span></label>
                      <BootstrapSelect
                        name="category"
                        value={formData.category ?? null}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        options={CATEGORY_OPTIONS}
                        placeholder="Select category"
                        disabled={submitting}
                      />
                    </div>
                    <div>
                      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Status</label>
                      <BootstrapSelect
                        name="status"
                        value={formData.isActive !== false ? 'Active' : 'Inactive'}
                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === 'Active' }))}
                        options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                        disabled={submitting}
                      />
                    </div>
                  </>
                )}
                {!config.hasCategory && config.hasStatus && entity !== 'products' && entity !== 'admins' && entity !== 'hospitals' && entity !== 'plansandfeatures' && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Status</label>
                    <BootstrapSelect
                      name="status"
                      value={formData.isActive !== false ? 'Active' : 'Inactive'}
                      onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === 'Active' }))}
                      options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                      disabled={submitting}
                    />
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                <button type="button" onClick={handleCloseModal} disabled={submitting} className="btn btn-secondary" style={{ padding: '10px 20px', borderRadius: '8px', fontWeight: '500', fontSize: '14px' }}>Cancel</button>
                <button type="submit" disabled={submitting} style={{ padding: '10px 20px', background: submitting ? '#cbd5e1' : BRAND_ORANGE, border: 'none', borderRadius: '8px', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {submitting ? <><i className="fas fa-spinner fa-spin"></i>Saving...</> : <><i className="fas fa-check"></i>{editingItem ? 'Update' : 'Add'} {config.title.slice(0, -1)}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        ::-webkit-scrollbar { width: 10px; height: 10px; }
        ::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        ::-webkit-scrollbar-thumb { background: #F89423; border-radius: 10px; border: 2px solid #f1f1f1; }
        ::-webkit-scrollbar-thumb:hover { background: #ea7c1f; }
        * { scrollbar-width: thin; scrollbar-color: #F89423 #f1f1f1; }
        .admin-dashboard-page { display: flex; min-height: 100vh; background-color: #f8f9fa; }
        .sidebar { width: 260px; background: white; position: fixed; left: 0; top: 0; height: 100vh; overflow-y: auto; transition: transform 0.3s ease; z-index: 1000; border-right: 1px solid #e5e7eb; }
        .sidebar-header { padding: 20px; border-bottom: 1px solid #e5e7eb; display: flex; align-items: center; }
        .brand-logo { color: #333; font-weight: 700; text-decoration: none; display: flex; align-items: center; }
        .sidebar-nav { padding: 20px 0; }
        .nav-section { margin-bottom: 25px; }
        .nav-section-title { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: #F89423; padding: 0 20px; margin-bottom: 10px; }
        .nav-list { list-style: none; padding: 0; margin: 0; }
        .nav-link { display: flex; align-items: center; padding: 12px 20px; color: #333; text-decoration: none; transition: all 0.3s ease; font-weight: 500; }
        .nav-link:hover, .nav-link.active { background-color: #F89423; color: white; }
        .sidebar-footer { margin-top: auto; padding-bottom: 20px; border-top: 1px solid #e5e7eb; padding-top: 20px; }
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; }
        .top-header { background: white; padding: 15px 30px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 100; }
        .header-left { display: flex; align-items: center; gap: 20px; }
        .header-right { display: flex; align-items: center; gap: 15px; }
        .header-icon { width: 40px; height: 40px; borderRadius: 50%; border: none; background: #f3f4f6; color: #6b7280; cursor: pointer; display: flex; align-items: center; justify-content: center; position: relative; }
        .header-icon:hover { background: #e5e7eb; color: #F89423; }
        .header-icon .badge { position: absolute; top: 5px; right: 5px; background: #F89423; color: white; font-size: 10px; padding: 2px 5px; border-radius: 10px; }
        .card { border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); background: white; border: none; }
        .p-card { border-radius: 12px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important; }
        .p-button { border-radius: 8px !important; font-weight: 500; }
        .p-inputtext, .form-control { border-radius: 8px !important; border: 1px solid #d1d5db !important; padding: 10px 14px !important; }
        .p-inputtext:focus, .form-control:focus { border-color: #F89423 !important; box-shadow: 0 0 0 2px rgba(248,148,35,0.2) !important; }
        .p-inputtextarea { border-radius: 8px !important; border: 1px solid #d1d5db !important; }
        .form-select { border-radius: 8px !important; border: 1px solid #d1d5db !important; padding: 10px 14px !important; }
        .form-select:focus { border-color: #F89423 !important; box-shadow: 0 0 0 2px rgba(248,148,35,0.2) !important; }
        .p-invalid, .is-invalid { border-color: #ef4444 !important; }
        .p-datatable .p-datatable-thead > tr > th { background: #f9fafb !important; font-weight: 600 !important; font-size: 13px !important; color: #374151 !important; }
        .p-datatable .p-datatable-tbody > tr > td { padding: 12px 16px !important; font-size: 14px !important; }
        .p-datatable .p-paginator { background: #f8f9fa !important; }
        .p-accordion .p-accordion-header .p-accordion-header-link {
          background: #f8f9fa !important;
          border-color: #e5e7eb !important;
          color: #374151 !important;
          font-weight: 600 !important;
          border-radius: 8px !important;
        }
        .p-accordion .p-accordion-header:not(.p-highlight):not(.p-disabled):hover .p-accordion-header-link {
          background: #fff7ed !important;
          border-color: #F89423 !important;
          color: #F89423 !important;
        }
        .p-accordion .p-accordion-header.p-highlight .p-accordion-header-link {
          background: #fff7ed !important;
          border-color: #F89423 !important;
          color: #F89423 !important;
          border-bottom-right-radius: 0 !important;
          border-bottom-left-radius: 0 !important;
        }
        .p-accordion .p-accordion-content {
          border-color: #e5e7eb !important;
          border-top: 0 none !important;
          background: white !important;
          padding: 20px !important;
        }
        @media (max-width: 992px) { .sidebar { transform: translateX(-100%); } .sidebar.sidebar-open { transform: translateX(0); } .main-content { margin-left: 0; } }
        @media (max-width: 768px) { .content-wrapper { padding: 15px; } .top-header { padding: 15px; } .modal-panel { width: 95% !important; } }
      `}</style>
    </div>
  );
}

export default AdminCRUDPage;